import { Injectable, WritableSignal, effect, signal, NgZone } from '@angular/core';
import { SurveyIndexService } from './survey-index.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription, of, from } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
// models
import { QuestionPage } from 'src/app/models/question_page';
import { SensorField } from 'src/app/models/sensor-field';
import { Survey } from 'src/app/models/survey';
import { Question } from 'src/app/models/question';
import { Answer, Partial_answers } from 'src/app/models/answer';
import { PreviewData } from 'src/app/models/preview-data';
import { ExtraData } from 'src/app/models/extra-data';
import { Alternative } from 'src/app/models/alternative';
import { AnswerCondition, ExtraDataCondition, LogicToShow, QuantityCondition } from 'src/app/models/logic-to-show';
import { Translate } from 'src/app/models/translate';
import { MatrixQuestion } from 'src/app/models/question-matrix';
import { PendingAnswers } from 'src/app/models/pending-answers';
// components
import { ModalConfirmComponent } from 'src/app/shared/components/modal-confirm/modal-confirm.component';
// globals
import { MatrixScaleConvertion, QuestionTypeGroups, QuestionTypes } from 'src/app/global/question-types';
import { LogicToShowConditionsTypes } from 'src/app/global/question-values';
// import { Validation_type } from 'src/app/global/validation_type';
// services
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { TranslateService } from 'src/app/services/translate.service';
import { NetworkService } from 'src/app/services/network.service';
import { LoadingScreenService } from 'src/app/services/loading-screen.service';
import { DbService } from 'src/app/services/db.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { QservusApiService } from 'src/app/services/qservus-api.service';
import { FirebaseService } from 'src/app/services/firebase.service';
import { RouterService } from 'src/app/services/router.service';
import { BrowserEventsService } from './browser-events.service';
import { QuestionFunctionalityService } from './question-functionality.service';
import { StorageManagerService } from './storage-manager.service';
import { DurationValidationService } from './duration-validation.service';
import { requestIdleOrTimeout, requestIdleOrTimeoutAsync } from '../utils/idle-helper';
import { forEachChunked } from '../utils/async-helpers';
// moment
import * as moment from 'moment';
import { isArray } from 'src/app/utils/utils';
// pipes
import { TransPipe } from 'src/app/shared/pipes/trans.pipe';
import { captureMessage } from '@sentry/angular';
import { CheckRutService } from './check-rut.service';
// env
// import { environment } from 'src/environments/environment';
import { ClientDurationTimestamps } from '../models/duration-validation';

@Injectable({
  providedIn: 'root'
})
export class SurveyService {
  public temp_answer: Record<string, unknown> | null = null;

  public surveyData = signal<Survey | null>(null);
  public surveyLoaded: boolean = false;
  public answers: Array<Answer> = [];
  public uuid_partial_answer: string = "";
  answers_changed: BehaviorSubject<boolean> = new BehaviorSubject(false);
  answers_change_status: boolean = false; // allow send partial answers

  private cache = new Map<string, { data: Survey; ttl: number }>();
  private readonly TTL = 5 * 60 * 1000; // 5 min

  answers_deleted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public reset_progress: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public current_page: QuestionPage = null;
  public current_page_index: number = 0;
  public last_page_index: number = 0;

  public show_sensor_form: boolean = false;

  public m_answers: number = 0;
  public total_for_progress: number = 0;
  public progressVisibilityEpoch: number = 0;

  public langs: Array<string> = [];

  public pending_answers: Array<any> = [];
  public processing_pending_answers: boolean = false;
  public processed_counter: number = 0;

  public call_check_progress: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public call_next_page: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public last_question_edited: number = null;

  public ip: string = "";

  public is_preview: boolean = false
  public is_widget: boolean = false
  public uuid_respondent: string = ""
  public uuid_survey: string = ""
  public api_domain: string = ""

  public qn!: number
  public a!: string
  public type!: number
  public channel!: string
  public google_api_called: boolean = false;

  public alternative_jump_to: number = -1
  public page_jump_to: number = -1
  public executeAutoPageAdvance: boolean = false;
  public lastAnswer: Answer = null;

  public loading_check_rut: boolean = false;
  private readonly activeJumpTargets: Map<number, number> = new Map();
  private readonly branchingLoadingKey: string = '__branching__';
  private branchingLoadingDepth: number = 0;
  private readonly skipPendingModalKey: string = 'skip_pending_modal_once';

  temp_answer_sub: Subscription;
  partial_answers!: Partial_answers;

  surveyRestarted: boolean = false;
  firstSavePartialAnswer : boolean  = true;
  checkSmartButton = signal(0);
  silentValidation: WritableSignal<boolean> = signal(false)
  loadingPage     : WritableSignal<{key: string, value: boolean}[]> = signal([])

  hideBannerOnThanksPage: boolean = false;

  start_datetime: string = "";
  last_interaction_datetime: string = "";

  navigations: WritableSignal<Array<number>> = signal([0]);

  constructor(
    private router: Router,
    private localStorageService: LocalStorageService,
    private trans: TranslateService,
    private networkService: NetworkService,
    private dialog: MatDialog,
    private loadingScreenService: LoadingScreenService,
    private dbService: DbService,
    private snack: SnackbarService,
    private transPipe: TransPipe,
    private qservusApiService: QservusApiService,
    private routerService: RouterService,
    private firebaseService: FirebaseService,
    private browserService: BrowserEventsService,
    private questionFunctionalityService: QuestionFunctionalityService,
    private checkRutService: CheckRutService,
    private storageManagerService: StorageManagerService,
    private durationValidationService: DurationValidationService,
    private indexService: SurveyIndexService,
    private ngZone: NgZone
  ) {
    // Defer getIP to idle time to avoid blocking constructor
    requestIdleOrTimeout(() => this.getIP(), 1000);
    
    this.browserService.getBrowserName()
    this.answers_changed.subscribe((next) => {
      if (next) {
        this.answers_change_status = true;
        // this.answers_changed.next(false);
      }

    })

    effect(() => {
      const navigationHistory = this.navigations();
      const uuid = this.uuid_survey?.trim();
      if (!uuid) return;
      this.storageManagerService.storeData(`navigations_${uuid}`, JSON.stringify(navigationHistory));
    });
    this.checkNetworkStatus()
  }

  loadSurvey(uuid: string) {
    const now = Date.now();
    if (this.cache.has(uuid) && this.cache.get(uuid)!.ttl > now) {
      return of(this.cache.get(uuid)!.data);
    }
    return from(this.qservusApiService.getSurvey(uuid)).pipe(
      switchMap(data => {
        this.surveyData.set(data);
        this.cache.set(uuid, { data, ttl: now + this.TTL });
        return of(data);
      }),
      shareReplay(1)
    );
  }

  async getIP() {
    if (!this.localStorageService.get("sip")) {
      const r = await this.networkService.getIP();
      this.ip = r["ip"];
      this.localStorageService.set("sip", this.ip);
    } else this.ip = this.localStorageService.get("sip");
  }

  /**
   * Set previous navigation from localStorage
   */
  setPreviousNavigation() {
    void this.loadPreviousNavigation();
  }

  /**
   * Load previous navigation history from storage.
   */
  async loadPreviousNavigation(): Promise<void> {
    try {
      const nav = await this.storageManagerService.getData<Array<number>>("navigations_" + this.uuid_survey);
      if (isArray(nav) && nav.length > 0) {
        this.navigations.set(nav);
      }
    } catch (error) {
      // fallback or ignore if not found
    }
  }

  /**
   * Remove the last navigation entry keeping the history immutable
   * so storage listeners detect the change.
   */
  trimNavigationHistory(): Array<number> {
    const history = this.navigations() ?? [];
    if (history.length <= 1) return history;
    const updatedHistory = history.slice(0, history.length - 1);
    this.navigations.set(updatedHistory);
    return updatedHistory;
  }

  /**
   * Ensure navigation history reflects the current page when resuming progress.
   */
  private syncNavigationHistoryWithCurrentPage(): void {
    const history = this.navigations() ?? [];
    const currentIndex = typeof this.current_page_index === 'number' ? this.current_page_index : 0;

    if (!history.length) {
      const fallback = currentIndex === 0 ? [0] : [0, currentIndex];
      this.navigations.set(fallback);
      return;
    }

    const lastOccurrence = history.lastIndexOf(currentIndex);

    if (lastOccurrence === -1) {
      this.navigations.set([...history, currentIndex]);
      return;
    }

    if (lastOccurrence < history.length - 1) {
      this.navigations.set(history.slice(0, lastOccurrence + 1));
    }
  }

  /**
   * Reset navigation history both in memory and persistent storage.
   */
  private clearNavigationHistory(): void {
    this.navigations.set([0]);
    const uuid = this.uuid_survey?.trim();
    // console.log("uuid", uuid);
    if (!uuid) return;
    this.storageManagerService.removeItem(`navigations_${uuid}`)
    
      .catch(error => console.error('Failed to remove navigation history from storage:', error));
  }
  /**
   * @Author Daniel García
   * @Date 20-06-2023
   * Check network status, if is online check pending answers
   */
  checkNetworkStatus() {
    this.networkService.status_sub.subscribe((value) => {
      if (value && !this.processing_pending_answers && this.pending_answers.length > 0) this.processPendingAnswers()
    })
  }

  /**
   * get question object by id
   * @param question_id 
   * @returns 
   */
  getQuestionById(question_id) {
    const questionId = typeof question_id === 'number' ? question_id : Number(question_id);
    const indexedQuestion = Number.isFinite(questionId) ? this.indexService.getQuestion(questionId) : undefined;
    if (indexedQuestion) return indexedQuestion;

    let question = null;
    this.surveyData().pages.every((p) => {
      question = p.questions.find(q => q.id == question_id);
      return question == undefined;
    });
    return question;
  }

  /**
   * get question object by order
   * @param order 
   * @returns 
   */
  getQuestionByQN(q_n: number) {
    const indexedQuestion = this.indexService.getQuestionByQN(q_n);
    if (indexedQuestion) return indexedQuestion;

    let question = null;
    const survey_questions = this.surveyData().pages.flatMap((p: QuestionPage) => p.questions);
    question = survey_questions.find((q: Question) => q.q_n == q_n);
    return question;
  }

  /**
   * Delete an answers depends on question_id property in answer object
   * @param question_id 
   */
  deleteAnswerByQuestionId(question_id: number) {
    const idx = this.answers.findIndex((a: Answer) => a.question_id == question_id)
    if (idx != -1) {
      this.answers.splice(idx, 1)
      this.indexService.removeAnswer(question_id);
      this.answers_deleted.next(true)
      this.answers_deleted.next(false)
    }
  }

  /**
   * Delete a matrix answer depends on matrix_id and question_id property in answer object
   * @param matrix_id 
   * @param question_id 
   */
  deleteMatrixAnswerByQuestionId(matrix_id: number, question_id: number) {
    const matrix_answers = this.answers.find((a: Answer) => a.question_id == matrix_id)
    const mq_answer = matrix_answers?.answer.find((a: any) => a.question.id == question_id)
    if (mq_answer != undefined) {
      mq_answer.alternative = null
      this.answers_deleted.next(true)
      this.answers_deleted.next(false)
    }
  }

  /**
   * Ensure MaxDiff questions reuse the same distribution that was originally assigned.
   * Uses the block stored along with the saved answers to rehydrate the question definition.
   */
  private applyStoredMaxdiffDistributions(): void {
    if (!this.surveyData()) {
      return;
    }

    const answersWithBlock = this.answers.filter((answer: Answer & { block_assigned?: number }) =>
      answer.type === QuestionTypes.MAXDIFF && !!answer['block_assigned']
    );

    if (!answersWithBlock.length) {
      return;
    }

    answersWithBlock.forEach((answer: Answer & { block_assigned?: number }) => {
      const storedBlock = answer.block_assigned;
      if (!storedBlock) {
        return;
      }

      const question = this.getQuestionById(answer.question_id);
      if (!question || !question.maxdiff_distributions?.length) {
        return;
      }

      if (question.maxdiff_block === storedBlock && question.maxdiff_pages?.length) {
        return;
      }

      const matchedDistribution = question.maxdiff_distributions.find(dist => dist.block_index === storedBlock);
      if (matchedDistribution) {
        question.maxdiff_block = matchedDistribution.block_index;
        question.maxdiff_pages = matchedDistribution.maxdiff_pages;
      }
    });
  }

  /**
   * check if exist partial answers for this survey
   */
  checkLocalAnswers() {
    return new Promise(async (resolve) => {
      await this.loadPreviousNavigation();
      
      const temporaryAnswers = this.uuid_respondent != "" ?
        await this.dbService.getTemporaryAnswersByProperty('respondent_uuid', this.uuid_respondent) :
        await this.dbService.getTemporaryAnswersByProperty('survey_uuid', this.qservusApiService.uuid4);

      if (temporaryAnswers.length) {
        if (temporaryAnswers[0].answers.length > 0) {
          this.answers = temporaryAnswers[0].answers;
          this.start_datetime = temporaryAnswers[0].start_datetime;
          this.last_interaction_datetime = temporaryAnswers[0].last_interaction_datetime ?? "";
          this.uuid_partial_answer = temporaryAnswers[0].uuid_partial_answer ? temporaryAnswers[0].uuid_partial_answer : "";
          this.answers.sort((a, b) => a.page - b.page);
          this.answers_changed.next(true);
          this.applyStoredMaxdiffDistributions();
          // console.log("checkLocalAnswers | local answers of incomplete survey =", this.answers);
          // check logic to show for all questions
          this.rebuildSurveyIndexes();
          await this.runInitialLogicEvaluation(true);
          this.executeAutoPageAdvance = true;
          if (this.navigations()) {
            const last_page_idx = this.navigations()[this.navigations().length - 1]
            if (last_page_idx < this.surveyData().pages.length) {
              this.current_page_index = last_page_idx;
              this.current_page = this.surveyData().pages[this.current_page_index];
              if (this.current_page.jump_to != undefined && this.current_page.jump_to > -1) {
                this.page_jump_to = this.current_page.jump_to;
                this.alternative_jump_to = -1;
              }
            }
          }
          this.syncNavigationHistoryWithCurrentPage();
          this.checkDerivationLogics();

          // this.executeAutoPageAdvance = true
          this.call_check_progress.next(true);
          if (!this.shouldSkipPendingModalOnce()) this.showModalPendingAnswer()
          resolve(true);
        } else {
          this.rebuildSurveyIndexes();
          await this.runInitialLogicEvaluation(false);
          this.call_check_progress.next(true);
          resolve(true);
        }
      } else {
        this.executeAutoPageAdvance = true
        //always check if survey has query params..
        await this.checkQueryParams();
        // not temporary answers answers saved locally so we need to verify if exists partials answers
        // first validate if survey has respondent and validate allow_partial_answers is true
        if (this.uuid_respondent != "" && this.surveyData().allow_partial_answers) {
          await this.getPartialAnswers().then( async (has_partial_answers) => {
            if (has_partial_answers) {
              this.answers_changed.next(true)
              // console.log("checkLocalAnswers | local answers of incomplete survey =", this.answers);
    
              const page_n_order = await this.getIncompletePageNOrder();
              // console.log("page_n_order", page_n_order);
    
              if (page_n_order != this.current_page.n_order) {
    
                const idx = this.surveyData().pages.findIndex(p => p.n_order == page_n_order);

                const page_to_load = this.surveyData().pages[idx];

                const has_visible_questions = this.hasVisibleQuestions(page_to_load);

                if (has_visible_questions) {
                  this.current_page = page_to_load
                  this.current_page_index = idx;
                } else {
                  this.current_page = this.surveyData().pages[0];
                  this.current_page_index = 0;
                }
              }
              if (this.answers.length > 0) this.syncNavigationHistoryWithCurrentPage();
              this.executeAutoPageAdvance = true
              // check logic to show for all questions
              this.rebuildSurveyIndexes();
              await this.runInitialLogicEvaluation(false);
    
              this.call_check_progress.next(true);              
              if (!this.shouldSkipPendingModalOnce()) this.showModalPendingAnswer()
            }
            if (this.surveyData().logic_operator) this.checkSensorLogicToShow()

              
            resolve(true);
          })
        }else{
          //means that is the fisrt time that the survey is loaded and the survey has not partial answers 
          this.executeAutoPageAdvance = true
        }
        this.rebuildSurveyIndexes();
        await this.runInitialLogicEvaluation(false);
        this.call_check_progress.next(true);
        resolve(true);
      }
    })
  }

  /**
 * update answer list from SurveyService
 * @param page current page id
 * @param question current question answered
 * @param response response data
 * @param valid: question validation status
 * @author Daniel García Silva
 * @date 2022-11-30
 */
  saveTemporaryAnswer(page: number, question: Question, response: any, valid: boolean) {
    if (response.answer == undefined) {
      //find answer and remove it
      let finded_answer = this.answers.find(a => a.question_id == question.id);
      if (finded_answer) {
        if (this.answers.length == 1) {
          this.answers = []
          this.indexService.updateAnswerIndex([]);
          this.dbService.deleteTemporaryAnswersBySurveyUUID()
        } else {
          this.answers.splice(this.answers.indexOf(finded_answer), 1);
        }
        this.indexService.removeAnswer(question.id);
      }
    } else {
      this.last_question_edited = question.id;
      let finded_answer = this.answers.find(a => a.question_id == question.id);

      if (!finded_answer) {
        // general
        this.answers.push({ page: page, question_id: question.id, type: question.type, answer: response.answer, valid: valid });
        finded_answer = this.answers.find((a: Answer) => a.question_id == question.id);
        // additional info
        if (response.other_text != undefined) finded_answer.other_text = response.other_text;
        if (response.alternative_id != undefined) finded_answer.alternative_id = response.alternative_id;
        if (response.motive != undefined) finded_answer.motive = response.motive;
        if (question.matrix_type != undefined) finded_answer.matrix_type = question.matrix_type;
        if (question.type === QuestionTypes.MAXDIFF && question.maxdiff_block != null) {
          finded_answer['block_assigned'] = question.maxdiff_block;
        }
        this.lastAnswer = null;
        this.indexService.setAnswer(finded_answer);
      } else {
        // general
        this.lastAnswer = { ...finded_answer };
        finded_answer.answer = response.answer;
        finded_answer.valid = valid;
        // aditional info
        if (response.other_text != undefined) finded_answer.other_text = response.other_text;
        if (response.alternative_id != undefined) finded_answer.alternative_id = response.alternative_id;
        if (response.motive != undefined) finded_answer.motive = response.motive;
        if (question.matrix_type != undefined) finded_answer.matrix_type = question.matrix_type;
        if (question.type === QuestionTypes.MAXDIFF && question.maxdiff_block != null) {
          finded_answer['block_assigned'] = question.maxdiff_block;
        }
        this.indexService.setAnswer(finded_answer);
      }
    }
    if (question['custom_question']) {
      this.updateSurveyExtraData(question, response)
    }
    this.touchLastInteractionTimestamp();
    this.checkDerivationLogics();
    this.recheckLogicForDependents(question.id, true);
    if (this.surveyData().logic_operator) this.checkSensorLogicToShow()
    if (this.surveyData().allow_partial_answers) this.savePartialAnswersInFirebase();
    
    this.answers.length ? this.saveTemporaryAnswerInIndexedDB() : null;
    this.answers_changed.next(true);
    this.call_check_progress.next(true);
    this.checkSmartButton.update(v => v + 1);
  }

  private touchLastInteractionTimestamp() {
    this.last_interaction_datetime = new Date().toISOString().slice(0, 19).replace('T', ' ');
  }

  getClientDurationSnapshot(): ClientDurationTimestamps {
    const snapshot: ClientDurationTimestamps = {
      start_datetime: this.start_datetime || null,
      end_datetime: this.last_interaction_datetime || null,
      duration_seconds: null
    };

    if (snapshot.start_datetime && snapshot.end_datetime) {
      const start = new Date(snapshot.start_datetime).getTime();
      const end = new Date(snapshot.end_datetime).getTime();
      if (!isNaN(start) && !isNaN(end)) snapshot.duration_seconds = Math.floor((end - start) / 1000);
    }

    return snapshot;
  }

  private getPendingDurationSnapshot(pending: PendingAnswers): ClientDurationTimestamps {
    const snapshot: ClientDurationTimestamps = pending.client_duration ? { ...pending.client_duration } : {
      start_datetime: pending.start_datetime ?? null,
      end_datetime: pending.client_end_datetime ?? null,
      duration_seconds: pending.duration_seconds ?? null
    };

    if (snapshot.duration_seconds == null && snapshot.start_datetime && snapshot.end_datetime) {
      const start = new Date(snapshot.start_datetime).getTime();
      const end = new Date(snapshot.end_datetime).getTime();
      if (!isNaN(start) && !isNaN(end)) snapshot.duration_seconds = Math.floor((end - start) / 1000);
    }

    return snapshot;
  }

  /**
   * Save temporary answers in indexedDB
   * @author Daniel García Silva
   * @date 2022-11-30
   */
  async saveTemporaryAnswerInIndexedDB() {
    const tempAnswerDB = this.uuid_respondent != "" ?
      await this.dbService.getTemporaryAnswersByProperty('respondent_uuid', this.uuid_respondent) :
      await this.dbService.getTemporaryAnswersByProperty('survey_uuid', this.qservusApiService.uuid4);

    const ans = this.answers.map(object => ({ ...object }));
    ans.filter((a: Answer) => a.type == 1 || a.type == 6)
      .forEach((a: Answer) => {

        let question: Question;
        this.surveyData().pages.every((p: QuestionPage) => {
          question = p.questions.find((q: Question) => q.id == a.question_id)
          return question == undefined
        })

        if (question) {
          if (question.type == 6 || (question.type == 1 && question.validation_type == "5" || question.validation_type == "DATE")) {
            if (moment.isMoment(a.answer)) a.answer = a.answer.format("DD/MM/YYYY");
            else {
              a.answer = moment(a.answer);
              a.answer = a.answer.format("DD/MM/YYYY");
            }
          }
        }

      })
    if (this.browserService.browser_name == 'safari') {
      ans.filter((a: Answer) => a.type == 16 || a.type == 30 || a.type == 31)
      ans.filter((a: Answer) => a.answer != null)
        .forEach((a: Answer) => {
          delete a.answer.file
        })
    }

    if (tempAnswerDB.length) {
      tempAnswerDB[0].answers = ans;
      tempAnswerDB[0].last_interaction_datetime = this.last_interaction_datetime;
      this.dbService.updateTemporaryAnswer(tempAnswerDB[0]);
    } else {
      const tempObj = {
        answers: ans,
        survey_uuid: "",
        start_datetime: this.start_datetime,
        last_interaction_datetime: this.last_interaction_datetime
      }
      if (this.uuid_respondent != "") tempObj['respondent_uuid'] = this.uuid_respondent
      this.dbService.pushTemporaryAnswer(tempObj);
    }
  }

  /**
   * save answers in Firebase to process as partial answers after visibilitychange browser event
   */
  async savePartialAnswersInFirebase() {
    if( this.firstSavePartialAnswer ){
      this.partialAnswerDataSubscription()
      this.firstSavePartialAnswer = false
    }

    if (this.networkService.getStatus()) { // online
      const has_files = await this.hasFileQuestions(this.answers);
      if (has_files) {

        this.firebaseService.AnonymousAuthentication().then(() => {
          this.uploadFilesToFirebase().then(async (res) => {
            const formated_answer = await this.formatProcessAnswer();
            formated_answer["update_at"] = Math.trunc(new Date().getTime() / 1000);
            formated_answer["api_domain"] = this.api_domain;
            await this.firebaseService.errorHandler('setDoc', formated_answer, "partial-answer-data/", [`${this.uuid_respondent ? this.uuid_respondent : this.ip}__${this.surveyData().id_survey.toString()}`]).then((resp) => {
              if (resp == null) captureMessage(`savePartialAnswersInFirebase: Error trying to set partial-answer-data document; api_domain: ${this.api_domain}.`);
            })
            this.temp_answer = formated_answer;
            // this.firebaseService.getDocByUid(doc_id, "partial-answer-data/").then((doc) => this.temp_answer = doc);
            this.answers_change_status = true;

          })
        })

      } else {
        // validate if formated_answer.answer is not empty array
        const formated_answer = await this.formatProcessAnswer();
        if(formated_answer.answers.length>0){
          this.firebaseService.AnonymousAuthentication().then(async () => {
            // validate if formated_answer.answer is not empty array
            formated_answer["update_at"] = Math.trunc(new Date().getTime() / 1000);
            formated_answer["api_domain"] = this.api_domain;
            await this.firebaseService.errorHandler('setDoc', formated_answer, "partial-answer-data/", [`${this.uuid_respondent ? this.uuid_respondent : this.ip}__${this.surveyData().id_survey.toString()}`]).then((resp) => {
              if (resp == null) captureMessage(`setAnswerDataInFirebase: Error trying to set partial-answer-data document; api_domain: ${this.api_domain}.`);
            })
            this.temp_answer = formated_answer;
            this.answers_change_status = true;
          })
        }
      }
    }

  }

  uploadFilesToFirebase() {
    return new Promise(async (resolve, reject) => {

      this.firebaseService.AnonymousAuthentication().then(async () => {
        for (const [i, a] of this.answers.entries()) {

          if (a.answer != null) {

            if (a.answer['file'] != undefined && a.answer["new_file"]) { // check file
              const question = this.getQuestionById(a.question_id);
              if (a.answer?.file_prev_ext != null) {
                await this.firebaseService.deleteFile(`partial-answers/${this.surveyData().id_survey}/${this.uuid_respondent ? this.uuid_respondent : this.ip}/`, `${question.id}${a.answer.file_prev_ext}`);
              }
              const r = await this.firebaseService.uploadFile(`partial-answers/${this.surveyData().id_survey}/${this.uuid_respondent ? this.uuid_respondent : this.ip}/`, `${question.id}${a.answer.file_ext}`, a.answer.file) as any;

              if (r.Location != undefined) {
                this.answers[i].answer["url"] = r.Location;
                this.answers[i].answer['new_file'] = false;
              }
              if (r == null) resolve(false);
            }
          }
          if (i == this.answers.length - 1) resolve(true);
        }
      })
    })
  }

  /**
	 * @author Matías Villar
	 * @date 07-12-2023
	 * @description subscription to partial-answer-data firebase document
	 */
  partialAnswerDataSubscription() {
    type PartialAnswerStatus = { status?: number; uuid_partial_answer?: string };
    let getDocTest = this.firebaseService.getDocByIdObservable<PartialAnswerStatus>(`${this.ip}__${this.surveyData().id_survey.toString()}`, "partial-answer-data/").subscribe( async (resp) => {
      if (resp != undefined && resp.status == 4 && typeof resp.uuid_partial_answer === 'string' && resp.uuid_partial_answer !== "") {
        const tempAnswerDB = this.uuid_respondent != "" ?
          await this.dbService.getTemporaryAnswersByProperty('respondent_uuid', this.uuid_respondent) :
          await this.dbService.getTemporaryAnswersByProperty('survey_uuid', this.qservusApiService.uuid4);

        if( tempAnswerDB[0] ){
          tempAnswerDB[0].uuid_partial_answer = resp.uuid_partial_answer
          this.dbService.updateTemporaryAnswer(tempAnswerDB[0]);
        }
        
        this.uuid_partial_answer = resp.uuid_partial_answer
        getDocTest.unsubscribe()
      }
    })
  }

  /**
   * 	check if the page to display has visible questions 
   * @param page 
   * @returns 
   */
  hasVisibleQuestions(page: QuestionPage) {
    let has_visibility = false;
    page.questions.every((q: Question) => {
      if (q.visibility && q.visibility_interpolation) {
        has_visibility = true;
        return false;
      } else return true;
    })
    return has_visibility;
  }

  /**
   * Check if exists any logic to show in all question
   * @param enable_answers_removal 
   */
  checkPageLogicToShow(enable_answers_removal: boolean = false) {
    // Use index instead of flatMap for better performance
    const all_questions = this.indexService.hasIndexes() 
      ? Array.from(this.indexService.getAllQuestions())
      : this.surveyData().pages.flatMap((p: QuestionPage) => p.questions);
    const matrixQuestionsToClean: Question[] = [];

    all_questions.forEach((q: Question) => {
      this.evaluateQuestionVisibility(q, enable_answers_removal, matrixQuestionsToClean);
    });

    if (matrixQuestionsToClean.length) {
      requestIdleOrTimeout(() => {
        this.cleanupMatrixVisibility(matrixQuestionsToClean);
      }, 0);
    }
  }

  /**
   * Evaluate logic for a specific set of pages.
   */
  checkPageLogicForPages(pages: QuestionPage[], enable_answers_removal: boolean = false): void {
    if (!pages?.length) return;
    const matrixQuestionsToClean: Question[] = [];

    pages.forEach((page) => {
      page.questions?.forEach((question) => {
        this.evaluateQuestionVisibility(question, enable_answers_removal, matrixQuestionsToClean);
      });
    });

    if (matrixQuestionsToClean.length) {
      requestIdleOrTimeout(() => {
        this.cleanupMatrixVisibility(matrixQuestionsToClean);
      }, 0);
    }
  }

  /**
   * Chunked version of checkPageLogicToShow for boot-time performance.
   * Ensures the same logic runs but yields between chunks.
   */
  async checkPageLogicToShowChunked(enable_answers_removal: boolean = false, chunkSize: number = 25): Promise<void> {
    const all_questions = this.indexService.hasIndexes()
      ? Array.from(this.indexService.getAllQuestions())
      : this.surveyData().pages.flatMap((p: QuestionPage) => p.questions);
    const matrixQuestionsToClean: Question[] = [];

    const runChunkedEvaluation = async () => {
      await forEachChunked(all_questions, chunkSize, (q: Question) => {
        this.evaluateQuestionVisibility(q, enable_answers_removal, matrixQuestionsToClean);
      });

      if (matrixQuestionsToClean.length) {
        requestIdleOrTimeout(() => {
          this.cleanupMatrixVisibility(matrixQuestionsToClean);
        }, 0);
      }
    };

    if (all_questions.length > 100) {
      await this.ngZone.runOutsideAngular(async () => {
        await runChunkedEvaluation();
      });
      this.ngZone.run(() => undefined);
      return;
    }

    await runChunkedEvaluation();
  }

  /**
   * Initial boot evaluation: evaluate current page first, defer the rest to idle.
   */
  async runInitialLogicEvaluation(enable_answers_removal: boolean = false): Promise<void> {
    if (!this.current_page || this.answers?.length) {
      await this.checkPageLogicToShowChunked(enable_answers_removal);
      return;
    }

    this.checkPageLogicForPages([this.current_page], enable_answers_removal);

    requestIdleOrTimeoutAsync(async () => {
      await this.checkPageLogicToShowChunked(enable_answers_removal);
    }, 1000);
  }

  /**
   * Incremental re-evaluation for logic depending on a changed answer.
   */
  recheckLogicForDependents(changedQuestionId: number, enable_answers_removal: boolean = false): void {
    if (!this.indexService.hasIndexes()) {
      this.checkPageLogicToShow(enable_answers_removal);
      return;
    }

    const matrixQuestionsToClean: Question[] = [];
    const queue: number[] = [changedQuestionId];
    const visited = new Set<number>();

    while (queue.length) {
      const sourceId = queue.shift();
      const dependents = this.indexService.getDependents(sourceId);
      dependents.forEach((dependentId) => {
        if (visited.has(dependentId)) return;
        visited.add(dependentId);

        const question = this.indexService.getQuestion(dependentId) ?? this.getQuestionById(dependentId);
        if (!question) return;

        const hadAnswer = !!this.indexService.getAnswer(dependentId);
        const previousVisibility = question.visibility;
        this.evaluateQuestionVisibility(question, enable_answers_removal, matrixQuestionsToClean);
        const hasAnswer = !!this.indexService.getAnswer(dependentId);

        if (previousVisibility !== question.visibility || (hadAnswer && !hasAnswer)) {
          queue.push(dependentId);
        }
      });
    }

    if (matrixQuestionsToClean.length) {
      setTimeout(() => {
        this.cleanupMatrixVisibility(matrixQuestionsToClean);
      }, 0);
    }
  }


  private evaluateQuestionVisibility(
    question: Question,
    enable_answers_removal: boolean,
    matrixQuestionsToClean: Question[]
  ): void {
    let show = question.visibility;
    if (question.logic_to_show) {
      show = this.checkLogicToShow(question, question.logic_to_show, this.surveyData(), true);
    }

    if (question.type == QuestionTypes.MATRIX && question.copy_alternatives_question_id != undefined) {
      if (question.logic_to_show == undefined && question.initial_visibility == undefined) {
        question.initial_visibility = question.visibility;
      }
      this.syncCarryForwardMatrixRows(question, this.surveyData());
      this.clearHiddenCarryForwardAnswers(question);
      const baseVisibility = question.logic_to_show ? question.visibility : (question.initial_visibility ?? question.visibility ?? true);
      question.visibility = this.hasVisibleMatrixRows(question) ? baseVisibility : false;
    } else if (question.copy_alternatives_question_id != undefined) {
      if (question.logic_to_show == undefined && question.initial_visibility == undefined) {
        question.initial_visibility = question.visibility;
      }
      this.syncCarryForwardAlternatives(question, this.surveyData());
      this.clearHiddenCarryForwardAnswers(question);
      const baseVisibility = question.logic_to_show ? question.visibility : (question.initial_visibility ?? question.visibility ?? true);
      question.visibility = this.hasVisibleCarryForwardAlternatives(question) ? baseVisibility : false;
    }

    question.alternatives?.forEach((alternative: Alternative, index: number) => {
      if (alternative.logic_to_show != undefined && alternative.logic_to_show != null) {
        this.checkAlternativeLogicToShow(question, alternative, alternative.logic_to_show, enable_answers_removal);
      }
      if (question.alternatives?.length - 1 == index) {
        const hasVisibleAlternatives = question.alternatives?.find(
          (a: Alternative) => a.visibility !== false && a.hidden !== true
        ) != undefined;
        if (hasVisibleAlternatives && show) {
          question.visibility = true;
        } else if (hasVisibleAlternatives && question.logic_to_show == undefined) {
          question.visibility = true;
        } else {
          question.visibility = false;
        }
      }
    });

    if (question.type == 100 && question.matrix_type == 9) {
      matrixQuestionsToClean.push(question);
    }
  }

  private cleanupMatrixVisibility(questions: Question[]): void {
    questions.forEach((q) => {
      q.matrix_questions?.forEach((mq: MatrixQuestion) => {
        if (!mq.visibility_interpolation) {
          const matrix_answers = this.answers.find((a: Answer) => a.question_id == q.id)
          const mq_answer = matrix_answers?.answer.find((a: any) => a.question.id == mq.id)
          if (mq_answer != undefined) mq_answer.alternative = null
        }
      })
      if (q.matrix_questions) {
        q.matrix_questions[0].alternatives.forEach((a: Alternative) => {
          if (a.visibility_interpolation == false) {
            const matrix_answers = this.answers.find((a: Answer) => a.question_id == q.id)
            if (matrix_answers && matrix_answers.answer) {
              const ma_filter = matrix_answers.answer.filter((ma: any) => ma.alternative != null).filter((ma: any) => ma.alternative.id == a.id)
              ma_filter.forEach((ma: any) => ma.alternative = null)
            }
          }
        })
      }
    });
  }

  /**
   * Sync visibility for carry-forward matrix rows using the current answers.
   * Only updates rows that have `carry_forward_alternative_id`.
   */
  private syncCarryForwardMatrixRows(question: Question, survey_data: Survey): void {
    if (!question?.matrix_questions?.length || question.copy_alternatives_question_id == undefined) return;
    this.questionFunctionalityService.copyAlternativesFromPreviusQuestionMatrixScale(question, survey_data, this.answers);
  }

  /**
   * Sync visibility for carry-forward alternatives using the current answers.
   * Only updates alternatives that have `carry_forward_alternative_id`.
   */
  private syncCarryForwardAlternatives(question: Question, survey_data: Survey): void {
    if (!question?.alternatives?.length || question.copy_alternatives_question_id == undefined) return;
    this.questionFunctionalityService.copyAlternativesFromPreviusQuestion(question, survey_data, this.answers);
  }

  /**
   * Checks if a matrix question has at least one visible row.
   * Rows without carry-forward are treated as visible unless explicitly hidden.
   */
  private hasVisibleMatrixRows(question: Question): boolean {
    if (!question?.matrix_questions?.length) return false;
    return question.matrix_questions.some((mq: MatrixQuestion) => mq.hidden !== true && mq.visibility_interpolation !== false);
  }

  /**
   * Checks if a carry-forward question has at least one visible alternative.
   * Alternatives without carry-forward are treated as visible unless hidden.
   */
  private hasVisibleCarryForwardAlternatives(question: Question): boolean {
    if (!question?.alternatives?.length) return false;
    return question.alternatives.some((alt: Alternative) =>
      alt.hidden !== true &&
      alt.visibility_interpolation !== false &&
      alt.visibility !== false
    );
  }

  /**
   * Removes stored answers that reference carry-forward rows/alternatives now hidden.
   * This prevents stale selections from persisting after the source answer changes.
   */
  private clearHiddenCarryForwardAnswers(question: Question): void {
    const answerIndex = this.answers.findIndex((a: Answer) => a.question_id == question.id);
    if (answerIndex === -1) return;

    const answer = this.answers[answerIndex];

    if (question.type == QuestionTypes.MATRIX) {
      const hiddenRowIds = new Set<number>(
        (question.matrix_questions ?? [])
          .filter((mq: MatrixQuestion) => mq.copied && mq.hidden === true)
          .map((mq: MatrixQuestion) => mq.id)
      );
      if (!hiddenRowIds.size || !Array.isArray(answer.answer)) return;

      let changed = false;
      answer.answer.forEach((row: any) => {
        if (row?.question?.id && hiddenRowIds.has(row.question.id) && row.alternative != null) {
          row.alternative = null;
          changed = true;
        }
      });
      if (changed) this.call_check_progress.next(true);
      return;
    }

    const hiddenAltIds = new Set<number>(
      (question.alternatives ?? [])
        .filter((alt: Alternative) => alt.copied && alt.hidden === true)
        .map((alt: Alternative) => alt.id)
    );
    if (!hiddenAltIds.size) return;

    if (Array.isArray(answer.answer)) {
      const filtered = answer.answer.filter((alt: Alternative) => !hiddenAltIds.has(alt.id));
      if (filtered.length !== answer.answer.length) {
        answer.answer = filtered;
        this.call_check_progress.next(true);
        if (filtered.length === 0) this.answers.splice(answerIndex, 1);
      }
      return;
    }

    if (answer.answer && typeof answer.answer === 'object' && 'id' in answer.answer) {
      if (hiddenAltIds.has(answer.answer.id)) {
        this.answers.splice(answerIndex, 1);
        this.call_check_progress.next(true);
      }
    }
  }

  /**
   * check if conditions of logic to show are fullfilled for questions
   * @param question question to evaluate
   * @param logic logic to show data
   * @param direction way on page change
   */
  checkLogicToShow(question: Question, logic: LogicToShow, survey_data: Survey, remove: boolean) {
    
    if (question.initial_visibility != undefined) question.initial_visibility = question.visibility;
    else question.initial_visibility = false

    const total_conditions = logic.conditions.length;
    let conditions_fullfiled = 0;
    logic.conditions.forEach((condition: any) => {

      // STRING "1", "2", "3" WILL BE REPLACED IN A FUTURE VERSION OR BRANCH, IN THIS BRANCH DOESN'T EXISTS GLOBAL TYPESCRIPT FILE WITH CONSTANTS. peepopray
      if (condition.type == LogicToShowConditionsTypes.ANSWER) {
        const ans_condition = condition as AnswerCondition;
        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == ans_condition.question_id);
        
        // find question - O(1) lookup via index
        const question = this.indexService.getQuestion(ans_condition.question_id as number);
        
        if (question != undefined && question.visibility && question.visibility_interpolation) {
          if (saved_answer != undefined) {
            // check type of answer // boolean | Array<number> | string
            if (typeof ans_condition.answer == "boolean") { // boolean answer
              // console.log("if boolean | saved_answer =", saved_answer);
              ans_condition.answer == saved_answer.answer ? conditions_fullfiled++ : null;

            } else if (Array.isArray(ans_condition.answer)) { // array answer

              const array_answers = ans_condition.answer as Array<string | number>;
              // saved answers array
              if (Array.isArray(saved_answer.answer)) {
                // console.log("saved is array");
                for (let ans of saved_answer.answer) {
                  if (ans != undefined) {
                    if (ans.value) if (array_answers.includes(ans.value?.toString())) conditions_fullfiled++;
                    else array_answers.includes(ans)
                  }
                }

              } else if (typeof saved_answer.answer == "object") {
                // console.log("saved is object");
                if (saved_answer.answer.value != undefined) {
                  if (array_answers.includes(saved_answer.answer.value.toString())) conditions_fullfiled++;
                }
              } else if (typeof saved_answer.answer == "number" || typeof saved_answer.answer == "string") {

                if (array_answers.includes(saved_answer.answer.toString())) conditions_fullfiled++
              }

            } else { // string answer
              if (condition.is_blank_answer != undefined) {
                if (condition.is_blank_answer) {
                  if (saved_answer.answer == "") conditions_fullfiled++;
                } else {
                  if (saved_answer.answer != "") conditions_fullfiled++;
                }
              } else {
                if (saved_answer.answer?.value != undefined) {
                  if (saved_answer.answer.value == ans_condition.answer) conditions_fullfiled++;
                } else if (saved_answer.answer == ans_condition.answer) conditions_fullfiled++;
              }

            }
          } else {
            if (condition.is_blank_answer != undefined) {
              if (condition.is_blank_answer) conditions_fullfiled++;
            }
          }
        }

      } else if (condition.type == LogicToShowConditionsTypes.EXTRA_DATA) { // extra data condition
        const extra_data_condition = condition as ExtraDataCondition;
        const survey_extra_data = survey_data.extra_data;
        let found = false;
        // check if extra_data exist (and must exist if it is a condition of this type...)
        if (survey_extra_data != undefined) {
          survey_data.extra_data.every((extra: ExtraData) => {
            if (extra_data_condition.extra_data == extra.property) {
              if (typeof extra_data_condition.value == 'string' || typeof extra_data_condition.value == 'number') {
                if (extra_data_condition.value == extra.value) {
                  conditions_fullfiled++;
                  found = true;
                  return false;
                }
              } else if (isArray(extra_data_condition.value)) {
                if ((extra_data_condition.value as Array<string | number>).includes(extra.value)) {
                  conditions_fullfiled++;
                  found = true;
                  return false;
                }
              }
            }
            return true;
          })
        }
        // Si no se encontró en extra_data, buscar en sensor_fields
        if (!found && survey_data.sensor_fields && Array.isArray(survey_data.sensor_fields)) {
          survey_data.sensor_fields.every((sensor_field: any) => {
            if (extra_data_condition.extra_data == sensor_field.label) {
              if (typeof extra_data_condition.value == 'string' || typeof extra_data_condition.value == 'number') {
                if (extra_data_condition.value == sensor_field.answer) {
                  conditions_fullfiled++;
                  found = true;
                  return false;
                }
              } else if (isArray(extra_data_condition.value)) {
                if ((extra_data_condition.value as Array<string | number>).includes(sensor_field.answer)) {
                  conditions_fullfiled++;
                  found = true;
                  return false;
                }
              }
            }
            return true;
          })
        }
      } else if (condition.type == LogicToShowConditionsTypes.QUANTITY) { // quantity
        const quantity_condition = condition as QuantityCondition
        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == quantity_condition.question_id)

        if (saved_answer != undefined) {

          if (Array.isArray(saved_answer.answer)) {
            const alt_count = saved_answer.answer.length
            if (alt_count == quantity_condition.quantity) conditions_fullfiled++
          }
        }
      }
    });

    if (logic.operator == 1) {
      if (conditions_fullfiled > 0) question.visibility = true
      else question.visibility = false
    } else {
      if (conditions_fullfiled == total_conditions) question.visibility = true
      else question.visibility = false
    }

    if (remove && !question.visibility) {
        const idx = this.answers.findIndex((a: Answer) => a.question_id == question.id);
        if (idx != -1 && remove) {
          this.answers.splice(idx, 1);
          this.indexService.removeAnswer(question.id);
        }
    }

    if (question.mandatory && this.surveyData()) this.call_check_progress.next(true);
    return question.visibility;
  }

  /**
   * check if conditions of logic to show are fullfilled for alternatives
   * @param question 
   * @param Alternative 
   * @param logic 
   */
  checkAlternativeLogicToShow(question: Question, alternative: Alternative, logic: LogicToShow, remove: boolean = false) {
    alternative.visibility = false;

    const total_conditions = logic.conditions.length;
    let conditions_fullfiled = 0;

    logic.conditions.forEach((condition: any) => {
      // console.log("condition", condition);

      if (condition.type == LogicToShowConditionsTypes.ANSWER) { // question condition
        const ans_condition = condition as AnswerCondition;
        // console.log("ans_condition", ans_condition);

        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == ans_condition.question_id);

        if (saved_answer != undefined) {
          // console.log("ans_condition.answer", ans_condition.answer);

          // check type of answer // boolean | Array<number>
          if (typeof ans_condition.answer == "boolean") { // boolean answer
            // console.log("if boolean | saved_answer =", saved_answer);
            ans_condition.answer == saved_answer.answer ? conditions_fullfiled++ : null;

          } else if (Array.isArray(ans_condition.answer)) { // array answer

            const array_answers = ans_condition.answer as Array<string | number>;
            // console.log("condition array_answers", array_answers);
            // saved answers array
            if (Array.isArray(saved_answer.answer)) {
              // console.log("saved is array");
              for (let ans of saved_answer.answer) {
                // saved_answer.answer.forEach((a: Array<any>) => {
                if (ans.value) if (array_answers.includes(ans.value?.toString())) conditions_fullfiled++;
                else array_answers.includes(ans)
              }

            } else if (typeof saved_answer.answer == "number" || typeof saved_answer.answer == "string") {
              if (array_answers.includes(saved_answer.answer.toString())) conditions_fullfiled++
            }

          } else { // string answer
            if (saved_answer.answer?.value != undefined) {
              if (saved_answer.answer.value == ans_condition.answer) conditions_fullfiled++;
            } else if (saved_answer.answer == ans_condition.answer) conditions_fullfiled++;

          }
        }

      }
      else if (condition.type == LogicToShowConditionsTypes.EXTRA_DATA) { // extra data condition
        const extra_data_condition = condition as ExtraDataCondition;

        const survey_extra_data = this.surveyData().extra_data;

        // check if extra_data exist (and must exist if it is a condition of this type...)
        if (survey_extra_data != undefined) {

          this.surveyData().extra_data.every((extra: ExtraData) => {

            if (extra_data_condition.extra_data == extra.property) {
              if (typeof extra_data_condition.value == 'string' || typeof extra_data_condition.value == 'number') {
                if (extra_data_condition.value == extra.value) {
                  conditions_fullfiled++;
                  return false;
                }
              } else if (isArray(extra_data_condition.value)) {
                if ((extra_data_condition.value as Array<string | number>).includes(extra.value)) {
                  conditions_fullfiled++;
                  return false;
                }
              }
            }
            return true;
          })
        }
      }
      else if (condition.type == LogicToShowConditionsTypes.QUANTITY) {  // quantity
        const quantity_condition = condition as QuantityCondition
        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == quantity_condition.question_id)

        if (saved_answer != undefined) {
          if (Array.isArray(saved_answer.answer)) {
            const alt_count = saved_answer.answer.length
            if (alt_count == quantity_condition.quantity) conditions_fullfiled++
          }
        }
      }
    });
    // evaluate result
    if (logic.operator == 1) {
      if (conditions_fullfiled > 0) alternative.visibility = true;
      else {
        const idx = this.answers.findIndex((a: Answer) => a.question_id == question.id);
        if (idx != -1) {
          // check answer type
          const ans = this.answers[idx];

          if (ans.alternative_id != undefined) { // scale numeric
            if (ans.alternative_id == alternative.id) {
              this.answers.splice(idx, 1);
              this.indexService.removeAnswer(question.id);
              this.call_check_progress.next(true);
            }
          } else if (ans.answer?.length > 0) { // select multi (alternative array)
            const idx_alt = ans.answer.findIndex((a: Alternative) => a.id == alternative.id);
            if (idx_alt != -1) {
              ans.answer.splice(idx_alt, 1);
              this.call_check_progress.next(true);
            }
          } else if (ans.answer?.id != undefined) { // select single (alternative object)
            if (ans.answer.id == alternative.id) {
              this.answers.splice(idx, 1);
              this.indexService.removeAnswer(question.id);
              this.call_check_progress.next(true);
            }
          }
        }
      }

    } else {
      if (conditions_fullfiled == total_conditions) alternative.visibility = true;
      else {
        // console.log("not fullfiled");
        const idx = this.answers.findIndex((a: Answer) => a.question_id == question.id);
        if (idx != -1 && remove) {
          // check type answer
          const ans = this.answers[idx];
          
          if (ans.alternative_id != undefined) { // scale numeric
            // console.log("scale numeric answer");
            if (ans.alternative_id == alternative.id) {
              this.answers.splice(idx, 1);
              this.indexService.removeAnswer(question.id);
              this.call_check_progress.next(true);
            }
          } else if (ans.answer?.length > 0) { // select multi (alternative array)
            // console.log("select multi answer");
            const idx_alt = ans.answer.findIndex((a: Alternative) => a.id == alternative.id);
            if (idx_alt != -1) {
              ans.answer.splice(idx_alt, 1);
              this.call_check_progress.next(true);
            }
          } else if (ans.answer?.id != undefined) { // select single (alternative object)
            // console.log("select single answer");
            if (ans.answer.id == alternative.id) {
              this.answers.splice(idx, 1);
              this.indexService.removeAnswer(question.id);
              this.call_check_progress.next(true);
            }
          }
        }
      }
    }
  }

  /**
   * check if conditions of logic to show are fullfilled for sensor fields
   * @param question question to evaluate
   * @param logic logic to show data
   * @param direction way on page change
   */
  checkSensorLogicToShow() {

    const total_conditions = this.surveyData().logic_fields?.length;
    let conditions_fullfiled = 0;
    this.surveyData().logic_fields?.forEach((condition: any) => {

      // console.log("condition", condition);

      if (condition.condition_type == LogicToShowConditionsTypes.ANSWER) {
        const ans_condition = condition as AnswerCondition;
        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == ans_condition.question_id);

        if (saved_answer != undefined) {

          const array_answers = ans_condition.alternatives as Array<{ "id": number, "value": string }>;
          // saved answers array
          if (Array.isArray(saved_answer.answer)) {
            for (let ans of saved_answer.answer) {
              if (ans != undefined) {
                if (array_answers.find((a: any) => a.id == ans.id)) conditions_fullfiled++;
              }
            }

          } else if (typeof saved_answer.answer == "object") {
            // console.log("saved is object");
            if (saved_answer.answer.value != undefined) {
              if (array_answers.find((a: any) => a.value == saved_answer.answer.value?.toString())) conditions_fullfiled++;
            } else if (saved_answer.alternative_id) {
              if (array_answers.find((a: any) => a.id == saved_answer.alternative_id.toString())) conditions_fullfiled++;
            }
          } else { // string answer
            if (saved_answer.answer?.value != undefined) {
              if (array_answers.find((a: any) => a.value == saved_answer.answer.value)) conditions_fullfiled++;
            } else if (array_answers.find((a: any) => a.id == parseInt(saved_answer.alternative_id.toString()))) conditions_fullfiled++;
          }

        }

      } else if (condition.condition_type == LogicToShowConditionsTypes.EXTRA_DATA) { // extra data condition
        const extra_data_condition = condition as ExtraDataCondition;

        const survey_extra_data = this.surveyData().extra_data;

        // check if extra_data exist (and must exist if it is a condition of this type...)
        if (survey_extra_data != undefined) {

          survey_extra_data.every((extra: ExtraData) => {

            if (extra_data_condition.extra_data_key == extra.property) {
              if (typeof extra_data_condition.extra_data_values == 'string' || typeof extra_data_condition.extra_data_values == 'number') {
                if (extra_data_condition.value == extra.value) {
                  conditions_fullfiled++;
                  return false;
                }
              } else if (isArray(extra_data_condition.extra_data_values)) {
                if ((extra_data_condition.extra_data_values as Array<string | number>).includes(extra.value)) {
                  conditions_fullfiled++;
                  return false;
                }
              }

            }
            return true;
          })
        }
      }
      else if (condition.condition_type == LogicToShowConditionsTypes.QUANTITY) { // quantity
        const quantity_condition = condition as QuantityCondition
        // find answer
        const saved_answer = this.answers.find((a: Answer) => a.question_id == quantity_condition.question_id)

        if (saved_answer != undefined) {
          if (Array.isArray(saved_answer.answer)) {
            const alt_count = saved_answer.answer.length
            if (alt_count == quantity_condition.quantity) conditions_fullfiled++
          }
        }
      }
    });
    if (this.surveyData().logic_fields != undefined) {
      if (this.surveyData().logic_operator == 1) {
        if (conditions_fullfiled > 0) this.surveyData().sensor_fields.forEach((field) => field.visibility = true)
        else this.surveyData().sensor_fields.forEach((field) => field.visibility = false)
      } else {
        if (conditions_fullfiled == total_conditions) this.surveyData().sensor_fields.forEach((field) => field.visibility = true)
        else this.surveyData().sensor_fields.forEach((field) => field.visibility = false)
      }
    }
    // Store sensor fields using the storage manager service
    // Store updated fields in background (autosave)
    void this.storageManagerService.storeFormFields(this.surveyData().id.toString(), this.surveyData().sensor_fields, {
      priority: 'autosave',
    }).catch(error => {
      console.error('[survey.service] Failed to store form fields:', error);
    });
  }

  /**
     * Helper method to get sensor fields from storage with fallback to localStorage
     * @returns Promise<Array<SensorField> | null>
     */
  private async getSensorFieldsFromStorage(): Promise<Array<SensorField> | null> {
    try {
      // Try to get from IndexedDB first
      const sensorFields = await this.storageManagerService.getFormFields<Array<SensorField>>(this.surveyData().id.toString());
      if (sensorFields) {
        return sensorFields;
      }
      
      // No fallback needed - storage manager handles IndexedDB/localStorage automatically
      return null;
    } catch (error) {
      console.error('Error getting sensor fields from storage:', error);
      return null;
    }
  }

  /** format responses to register data in QServus */
  async formatProcessAnswer() {
    try {
      const clientDuration = this.getClientDurationSnapshot();
      let survey = {
        "su_survey_id": this.surveyData().id_survey,
        "ip": this.ip,
        "user_agent": window.navigator.userAgent,
        "answers": [],
        "extra_data": [],
        "respondent_data": {},
        "status": 0,
        "uuid_partial_answer": this.uuid_partial_answer,
        "start_datetime": this.start_datetime,
        "client_end_datetime": clientDuration.end_datetime ?? null,
        "duration_seconds": clientDuration.duration_seconds ?? null,
        "client_duration": clientDuration,
        "duration_validation_reason": null
      }
      if (this.uuid_respondent) {
        survey["uuid_respondent"] = this.uuid_respondent;
      }

      const surveyType = (this.surveyData().type || '').toLowerCase();
      if (surveyType === "campaña" || surveyType === "campaign") {
        // survey["qs_campaign_id"] = this.survey_data.id;
        if (this.surveyData().title.es !== undefined && this.surveyData().title["es"].includes("Entrega Dedicada")) this.configureCustomRespondentData(survey);

      } else if (this.surveyData().type == "sensor") {
        survey["respondent_data"] = {
          name: "",
          email: "",
          phone: ""
        }
        // survey["qs_sensor_id"] = this.survey_data.id;
        // add sensor fields
        const sensor_field_json = await this.getSensorFieldsFromStorage();

        if (sensor_field_json != null && sensor_field_json.length > 0) {
          sensor_field_json
            .filter((sf: SensorField) => sf.label == 'EMAIL' || sf.label == 'PHONE' || sf.label == "NAME")
            .filter((sf: SensorField) => sf.visibility)
            .forEach((sf: SensorField) => {
              survey["respondent_data"][sf.label.toString().toLowerCase()] = sf.answer != undefined ? sf.answer : "";
            })
          const extra_data = sensor_field_json.filter((sf: SensorField) => sf.label != 'EMAIL' && sf.label != 'PHONE' && sf.label != "NAME");

          extra_data.forEach(sf => {
            survey.extra_data.push({
              "label": sf.label,
              "value": sf.answer
            })
          })

        } else this.configureCustomRespondentData(survey);

      } else if (this.surveyData().type == "checklist") {

        // survey["qs_checklist_id"] = this.survey_data.id;
        // add sensor fields using storage manager
        const sensor_fields = await this.getSensorFieldsFromStorage();
        let sensor_field_json = sensor_fields;

        if (sensor_field_json != null && sensor_field_json.length > 0) {
          sensor_field_json
            .filter((sf: SensorField) => sf.label == 'EMAIL' || sf.label == 'PHONE' || sf.label == "NAME")
            .forEach((sf: SensorField) => {
              survey["respondent_data"][sf.label.toString().toLowerCase()] = sf.answer != undefined ? sf.answer : "";
            })
          const extra_data = sensor_field_json.filter((sf: SensorField) => sf.label != 'EMAIL' && sf.label != 'PHONE' && sf.label != "NAME");

          extra_data.forEach(sf => {
            survey.extra_data.push({
              "label": sf.label,
              "value": sf.answer
            })
          })
        } else this.configureCustomRespondentData(survey);
      }

      if (this.surveyData().title.es !== undefined && this.surveyData().title["es"].includes("Entrega Dedicada")) {
        survey.extra_data.push({
          "label": "tipo_entrega",
          "value": this.surveyData().title["es"].includes("nuevo") ? "nuevo" : "usado"
        })
        const sales_number = this.surveyData().pages[0].questions.find((q) => q.title['es'].includes('nota de venta'));
        if (sales_number != undefined) {
          const question_sales_number_id = sales_number.id;
          const answer = this.answers.find(a => a.question_id == question_sales_number_id)
          if (answer != undefined) {
            survey.extra_data.push({
              "label": "numero_nota_venta",
              "value": answer.answer != undefined ? answer.answer : null
            })
          }
        }
        const customer_dni = this.surveyData().pages[0].questions.find((q) => q.title['es'].includes('Rut') || q.title['es'].includes('DNI'));
        if (customer_dni != undefined) {
          const question_customer_dni_id = customer_dni.id;
          const answer = this.answers.find(a => a.question_id == question_customer_dni_id)
          if (answer != undefined) {
            survey.extra_data.push({
              "label": "identificador_cliente",
              "value": answer.answer != undefined ? answer.answer : null
            })
          }
        }
      }
      const all_questions = this.surveyData().pages.flatMap( (p: QuestionPage) => p.questions)

      this.answers.sort((a, b) => a.question_id - b.question_id);
      this.answers.filter((answer: Answer) => {
        return all_questions.find((q: Question) => q.id == answer.question_id && (q.visibility && q.visibility_interpolation)) != undefined
      }).forEach((answer: Answer, i: number) => {

        let question = null;
        if (answer.type == 1) question = this.getQuestionById(answer.question_id);
        // console.log("answer " + ++i + " =", answer);
        let ans_to_add = {
          "su_question_id": answer.question_id,
          "type": answer.type,
          "answer": null
        }
        // format answer
        if (question == null) this.formatAnswerDependOnQuestionType(answer, ans_to_add, survey);
        else this.formatAnswerDependOnQuestionType(answer, ans_to_add, survey, question.validation_type != null ? question.validation_type : null);
        
        
        if (answer.type == QuestionTypes.MAXDIFF) {
          question = this.getQuestionById(answer.question_id);
          ans_to_add['block_assigned'] = question.maxdiff_block;
        }
        survey.answers.push(ans_to_add);
      })
      // filter matrix questions
      survey.answers = survey.answers.filter((a: Answer) => a.type != 100).filter((a: Answer) => a.answer != null);

      this.surveyData().extra_data.forEach((ed: ExtraData) => {  
        if (survey.extra_data.find((e: any) => e.label == ed.property) == undefined) {
          survey.extra_data.push({
            "label": ed.property,
            "value": ed.value
          })
        }
      })
      // logic to remove answers with id < 0 and add it as extra_data
      // const extra_questions = survey.answers.filter((a) => a.su_question_id == -9 || a.su_question_id == -10 || a.su_question_id == -11 || a.su_question_id == -12);

      // if (extra_questions.length > 0) {
      //   // looping through answer list
      //   extra_questions.forEach(((a) => {
      //     let property_name = "";
      //     if (a.su_question_id == -9) property_name = "Sucursal";
      //     else if (a.su_question_id == -10) property_name = "Marca";
      //     else if (a.su_question_id == -12) property_name = "Modelo";
      //     else if (a.su_question_id == -11) property_name = "notification_channel";

      //     survey.extra_data.push({
      //       "label": property_name,
      //       "value": a.answer
      //     })

      //   }))
      // }
      return survey;
    } catch (error) {
      // console.log("formatProcessAnswer error", error);
      return null
    }
  }

  /**
   * get the answer in the format needed to register it in QServus
   * @param saved_answer 
   * @param ans_to_add 
   */
  formatAnswerDependOnQuestionType(saved_answer: Answer, ans_to_add: any, survey: any, validation_type?: number | string) {
    const type = this.getQuestionGroup(saved_answer.type);
    if (type == "TEXT") {
      ans_to_add.answer = saved_answer.answer;
      if (validation_type != undefined && validation_type != null) {
        if (validation_type == 5 || validation_type == "DATE") {
          if (moment.isMoment(saved_answer.answer)) ans_to_add.answer = saved_answer.answer.format("DD/MM/YYYY");
          else {
            saved_answer.answer = moment(saved_answer.answer);
            ans_to_add.answer = saved_answer.answer.format("DD/MM/YYYY");
          }
        }
      }
      // if (moment.isMoment(saved_answer.answer)) ans_to_add.answer = saved_answer.answer.toISOString();
      if (saved_answer.answer.extra_data != undefined) {
        ans_to_add.answer = saved_answer.answer.text;
        Object.keys(saved_answer.answer.extra_data).forEach((key) => {
          // console.log(survey);

          survey.extra_data.push({
            "label": key,
            "value": saved_answer.answer.extra_data[key]
          })
        })
      }
    }
    else if (type == "DATE") {
      // if (moment.isMoment(saved_answer.answer)) ans_to_add.answer = saved_answer.answer.toISOString();
      if (moment.isMoment(saved_answer.answer)) ans_to_add.answer = saved_answer.answer.format("DD/MM/YYYY");
      else {
        saved_answer.answer = moment(saved_answer.answer);
        ans_to_add.answer = saved_answer.answer.format("DD/MM/YYYY");
      }
    }
    else if (type == "SINGLE_SELECTION") {
      if (typeof saved_answer.answer == "string" || typeof saved_answer.answer == "number") ans_to_add.answer = saved_answer.answer;
      else ans_to_add.answer = saved_answer.answer != null ? saved_answer.answer.value?.toString() : null;
      // add motive
      if (saved_answer.motive != undefined) if (saved_answer.motive != "") ans_to_add["answer_motive"] = saved_answer.motive;
      // add other option
      if (saved_answer.other_text != undefined) if (saved_answer.other_text != "") ans_to_add["answer_other"] = saved_answer.other_text;
    }
    else if (type == "MULTI_SELECTION") {
      const r = saved_answer.answer.map((a: Alternative) => { return a.value }) as Array<any>;
      if (r != undefined) ans_to_add.answer = r.toString();
      // add other option
      if (saved_answer.other_text != undefined) if (saved_answer.other_text != "") ans_to_add["answer_other"] = saved_answer.other_text;
    }
    else if (type == "BOOLEAN") ans_to_add.answer = saved_answer.answer;
    else if (type == "RANKING") {
      if (saved_answer.answer != null) {
        //remove answer with visivility interpolaiton = false
        saved_answer.answer = saved_answer.answer.filter(answer => answer.visibility_interpolation == true)
        ans_to_add.answer = saved_answer.answer.map((a, i) => {
          return { 'order': ++i, 'alternative': a.id }
        })
      }
    }
    else if (type == "FILE") {
      if (this.networkService.getStatus()) {
        if (saved_answer.answer != null) {
          ans_to_add.answer = saved_answer.answer.url;
        }
      } else ans_to_add.answer = saved_answer.answer;
    }
    else if (type == "MATRIX") {
      // console.log("matrix answers", saved_answer.answer);
      // sacar id de tipo de escala
      saved_answer.answer.forEach(ma => { // loop in matrix answers
        // console.log("saved_answer.matrix_type", saved_answer.matrix_type);
        let ma_answer = null;
        let matrix_questions_type = MatrixScaleConvertion[saved_answer.matrix_type];

        if (ma.alternative != undefined) if (ma.alternative != null) {
          if (!isArray(ma.alternative)) {
            ma_answer = ma.alternative.value.toString();
          } else { // selection multi
            const r = ma.alternative.map((a: Alternative) => { return a.value }) as Array<any>;
            ma_answer = r.toString();
            matrix_questions_type = 3;
          }
        }

        survey.answers.push({
          "su_question_id": ma.question.id,
          "type": matrix_questions_type,
          "answer": ma_answer
        });

      });
    }
    else if (type == "MAX_DIFF") {
      ans_to_add.answer = saved_answer.answer;
    } else if (type == "CONJOINT") {
      // find conjoint question
      const conjoint_question = this.getQuestionById(saved_answer.question_id);
      let cj_parsed_answer = [];

      saved_answer.answer.forEach((cj_answer: any) => {
        let cj_answer_obj = {
          "page_id": cj_answer.page_id,
          "option_id": cj_answer.option_id
        }
        cj_parsed_answer.push(cj_answer_obj);
      })

      ans_to_add.su_question_id = saved_answer.question_id;
      ans_to_add.type = saved_answer.type;
      ans_to_add.conjoint_block = conjoint_question.conjoint_block;
      ans_to_add.answer = cj_parsed_answer
    }

  }

  /**
   * Add properties requiered to save answers on "Entrega Dedicada" campaigns.
   * @param survey 
   */
  configureCustomRespondentData(survey: any) {
    // get name
    let question_name_id, question_email_id, question_phone_id = null;

    const q_name = this.surveyData().pages[0].questions.find((q) => q.title['es'] == 'Nombre');
    const q_email = this.surveyData().pages[0].questions.find((q) => q.title['es'] == 'Correo');
    const q_phone = this.surveyData().pages[0].questions.find((q) => q.title['es'] == 'Teléfono');
    if (q_name != undefined) {
      question_name_id = q_name.id;
      const answer_obj = this.answers.find(a => a.question_id == question_name_id)
      if (answer_obj != undefined) survey.respondent_data.name = answer_obj.answer;
    }
    if (q_email != undefined) {
      question_email_id = q_email.id;
      const answer_obj = this.answers.find(a => a.question_id == question_email_id)
      if (answer_obj != undefined) survey.respondent_data.email = answer_obj.answer;
    }
    if (q_phone != undefined) {
      question_phone_id = q_phone.id;
      const answer_obj = this.answers.find(a => a.question_id == question_phone_id)
      if (answer_obj != undefined) survey.respondent_data.phone = answer_obj.answer;
    }
  }

  /** format responses to register data in Firebase */
  formatPreviewData(respondent_uuid: string, survey_formated: any): PreviewData {

    let preview: PreviewData = null;

    this.answers.forEach((a: Answer) => {
      let finded_question: Question;
      this.surveyData().pages.every((p: QuestionPage) => {
        finded_question = p.questions.find((q: Question) => q.id == a.question_id);
        return finded_question == undefined
      })
      // console.log("finded_question", finded_question);

      const add = {
        title: finded_question.title[this.trans.actualLang],
        help_text: finded_question.help_text != undefined ? finded_question.help_text[this.trans.actualLang] : null,
        mandatory: finded_question.mandatory,
        question_type: finded_question.type,
        validation_type: finded_question.validation_type != undefined ? finded_question.validation_type : null,
        motive: a.motive != undefined ? a.motive : '',
        question_id: a.question_id,
        other_text: a.other_text != undefined ? a.other_text : '',
        answer: a.answer,
        order: finded_question.order_preview != undefined ? finded_question.order_preview : finded_question.order
      }
      if (finded_question.validation_type == "5" || finded_question.type == 6) {
        if (moment.isMoment(add.answer)) add.answer = add.answer.toISOString();
        add.answer = new Date(add.answer).getTime();
      }
      if (finded_question.type == 3) {
        add['not_selected'] = finded_question.alternatives.filter((alt1: Alternative) => !a.answer.some((alt2: any) => alt2.id == alt1.id));
      }
      if (finded_question.type == 16 || finded_question.type == 30 || finded_question.type == 31) {
        add.answer = Object.assign({}, a.answer);
        delete add.answer.file;
        delete add.answer.base64;
        delete add.answer.file_ext;

      }
      if (preview == null) {
        preview = {
          respondent_uuid: respondent_uuid,
          status: 0,
          created_at: moment().valueOf(),
          status_changed_at: null,
          banner: this.surveyData().logo,
          questions: [add],
          language: this.trans.actualLang,
          extra_data: survey_formated.extra_data != undefined ? survey_formated.extra_data : null,
          api_domain: this.api_domain,
          dedicated_delivery: this.surveyData().title[this.trans.actualLang].includes('Entrega Dedicada')
        }

      } else preview.questions.push(add);
    });
    preview.questions.sort((a, b) => a.order - b.order)
    return preview;
  }

  /**
   * check if survey has OTP question
   */
  hasOTPValidation() {
    return this.surveyData().validate_otp != undefined ? this.surveyData().validate_otp : false;
  }

  /**
   * reset all data from previous respondent
   */
  resetTemporarySurveyData() {
    // console.log("temporary answers will be deleted");    
    this.storageManagerService.removeItem('construct-'+ this.uuid_survey).catch(error => {
      console.error('Failed to remove survey construct from storage:', error);
    });
    this.clearNavigationHistory();
    this.surveyLoaded = false;
    this.answers = [];
    this.indexService.updateAnswerIndex([]);
    this.answers_deleted.next(true);
    this.answers_deleted.next(false);
    this.answers_changed.next(true)
    // Notify question components to clear their form values
    this.answers_deleted.next(true);
    this.answers_deleted.next(false);
    this.m_answers = 0;
    this.activeJumpTargets.clear();
    this.progressVisibilityEpoch++;
    this.current_page = this.surveyData().pages.find((p: QuestionPage, i: number) => p.questions.length > 0 || (p.questions.length == 0 && p.show_page_title))
		this.current_page_index = this.surveyData().pages.findIndex((p: QuestionPage) => p == this.current_page)
    const has_visible_questions = this.hasVisibleQuestions(this.current_page)
		if (!has_visible_questions) this.call_next_page.next(true)
    this.checkPageLogicToShow();
    this.surveyRestarted = true;
    this.start_datetime = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.last_interaction_datetime = "";
    this.reset_progress.next(true);
    this.temp_answer_sub?.unsubscribe();
    this.dbService.deleteTemporaryAnswersBySurveyUUID();
    this.checkRutService.validation_executed = false;
    if (this.surveyData() && this.networkService.getStatus()) this.firebaseService.deleteDoc(`${this.uuid_respondent ? this.uuid_respondent : this.ip}__${this.surveyData().id_survey.toString()}`, "partial-answer-data")
    setTimeout(() => {
      this.surveyLoaded = true;
      this.checkSmartButton.update(v => v + 1);
    }, 1);
  }
  /** clear survey properties and redirect to thanks page */
  finishSurvey() {
    // check if survey is a CMT survey
    if (this.surveyData().id == 310309 || this.surveyData().id == 310311 || this.surveyData().id == 10190 || this.surveyData().id == 10296) {
      // get the first answer and check if the answer value is 4 or 5
      const first_answer = this.answers.find((a: Answer) => a.type == 12) 
      if (first_answer != undefined) {
        if (first_answer.answer == "4" || first_answer.answer == "5") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }
    }
    // check if survey is a JUAN VALDEZ survey
    if (this.surveyData().id == 1064 || this.surveyData().id == 1063 || this.surveyData().id == 1062 || this.surveyData().id == 1061 || this.surveyData().id == 1060 || this.surveyData().id == 1059 || this.surveyData().id == 1058) { 
      // get the first answer and check if the answer value is 9 or 10
      const first_answer = this.answers.find((a: Answer) => a.type == 14)
      if (first_answer != undefined) {
        if (first_answer.answer == "9" || first_answer.answer == "10") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }   
    }
    // check if survey is a Verisure Argentina survey
    if (this.surveyData().id == 314422 || this.surveyData().id == 313221 || this.surveyData().id == 313220 || this.surveyData().id == 313219) {
      const first_answer = this.answers.find((a: Answer) => a.type == 14) 
      // check if the first answer is defined and if the answer value is 9 or 10 
      if (first_answer != undefined) {
        if (first_answer.answer == "9" || first_answer.answer == "10") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }
    }
    // check if survey is a Verisure Chile survey
    if (this.surveyData().id == 10847 ||
      this.surveyData().id == 309893 ||
      this.surveyData().id == 310026 ||
      this.surveyData().id == 309901 ||
      this.surveyData().id == 309902 ||
      this.surveyData().id == 313344 ||
      this.surveyData().id == 310006 ||
      this.surveyData().id == 309904 ||
      this.surveyData().id == 312930 ||
      this.surveyData().id == 309900 ||
      this.surveyData().id == 309898 ||
      this.surveyData().id == 309953 ||
      this.surveyData().id == 313313 ||
      this.surveyData().id == 313664 ||
      this.surveyData().id == 310130 ||
      this.surveyData().id == 310348 ||
      this.surveyData().id == 313605 ||
      this.surveyData().id == 314418 ||
      this.surveyData().id == 314083 ||
      this.surveyData().id == 309899) {
      const first_answer = this.answers.find((a: Answer) => a.type == 14) 
      // check if the first answer is defined and if the answer value is 9 or 10 
      if (first_answer != undefined) {
        if (first_answer.answer == "9" || first_answer.answer == "10") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }
    }
    // verisure Perú
    const campaignsWithExternalRedirect: Array<{ id: number; qn: number | number[] }> = [
      { id: 8951, qn: [27, 31] }, // Verisure Perú (pruebas)
      { id: 8778, qn: 13 },
      { id: 8776, qn: [13, 17] },
      { id: 8950, qn: [13, 18] },
      { id: 9355, qn: 23 },
      { id: 9430, qn: 15 },
      { id: 9483, qn: 23 },
      { id: 9603, qn: 17 },
      { id: 10633, qn: 28 },
      { id: 10632, qn: 18 },
      { id: 10581, qn: 20 },
      { id: 11473, qn: 1 },
      { id: 9360, qn: 1 },
      { id: 309162, qn: 15 },
      { id: 309171, qn: [13, 17] },
      { id: 309332, qn: [27, 31] },
      { id: 309333, qn: [13, 18] },
      { id: 309691, qn: 23 },
      { id: 309759, qn: 15 },
      { id: 309810, qn: 23 },
      { id: 309909, qn: 17 },
      { id: 312956, qn: 28 },
      { id: 312957, qn: 18 },
      { id: 313006, qn: 20 },
      { id: 313758, qn: 1 },
      { id: 309690, qn: 1 }
    ];

    const campaignConfig = campaignsWithExternalRedirect.find((campaign) => campaign.id == this.surveyData().id);
    if (campaignConfig) {
      const qns = Array.isArray(campaignConfig.qn) ? campaignConfig.qn : [campaignConfig.qn];

      const shouldRedirect = qns.every((qn) => {
        const targetQuestion = this.getQuestionByQN(qn);
        const targetAnswer = targetQuestion ? this.answers.find((a: Answer) => a.question_id == targetQuestion.id) : undefined;

        if (!targetQuestion || !targetAnswer || targetAnswer.answer == undefined) {
          return false;
        }

        const rawValue: any = targetAnswer.answer;
        const normalizedValue = (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) ? rawValue['value'] : rawValue;
        const numericValue = Number(normalizedValue);

        if (Number.isNaN(numericValue)) {
          return false;
        }

        return (
          (targetQuestion.type == 14 && (numericValue == 9 || numericValue == 10)) ||
          (targetQuestion.type == 12 && (numericValue == 4 || numericValue == 5))
        );
      });

      if (shouldRedirect) {
        this.router.navigate(['/thanks/external']).then(() => {
          this.resetTemporarySurveyData();
        });
        return;
      }
    }

    // check if survey is a Redinterclinica survey - Cordillera
    if (this.surveyData().id == 10841 || this.surveyData().id == 7097 || this.surveyData().id == 7098 || this.surveyData().id == 7135 || this.surveyData().id == 7100 || this.surveyData().id == 8702) {
      const first_answer = this.answers.find((a: Answer) => a.type == 14)
      if (first_answer != undefined) {
        if (first_answer.answer == "9" || first_answer.answer == "10") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }
    }
    // check if survey is a Sanatorio Alemán survey
    if (this.surveyData().id_survey == 336533 || this.surveyData().id_survey == 336534 || this.surveyData().id_survey == 336535 || this.surveyData().id_survey == 336536 || this.surveyData().id_survey == 336537 || this.surveyData().id_survey == 346099 || this.surveyData().id_survey == 346119 || this.surveyData().id_survey == 346124 || this.surveyData().id_survey == 347113) {
      const first_answer = this.answers.find((a: Answer) => a.type == 14)
      if (first_answer != undefined) {
        if (first_answer.answer == "9" || first_answer.answer == "10") {
          this.router.navigate(['/thanks/external']).then(() => {
            this.resetTemporarySurveyData();
          });
          return;
        }
      }
    }
    // check if survey is a Consalud survey - custom end message
    if ((this.surveyData().id_survey == 77 || this.surveyData().id_survey == 128) && this.surveyData().title.es.includes("Consalud")) {  
      // necesito encontrar la respuesta a la pregunta con q_n = 9
      const question_qn9 = this.surveyData().pages.flatMap((p: QuestionPage) => p.questions).find((q: Question) => q.q_n == 9);
      if (question_qn9 != undefined) {
        const answer_qn9 = this.answers.find((a: Answer) => a.question_id == question_qn9.id);
        if (answer_qn9 != undefined) {
          if (answer_qn9.answer.value == 1) {
            this.surveyData().confirm_central_paragraph.es = "Nos pondremos en contacto contigo para orientarte y puedas entender totalmente tu plan en Consalud."
            this.surveyData().confirm_end_paragraph.es = "¡Que tengas un buen día!"
          }
        }
      }
    }
    this.router.navigate(['/thanks']).then(() => {
      this.resetTemporarySurveyData();
    });
  }
  /**
   * check if some of the temporary questions is not valid and return the value of the page in where it is
   * @returns page number
   */
  async getIncompletePageNOrder() {
    const n_page = await new Promise((res, rej) => {
      // mark as invalid all file type questions (16, 30, 31)
      // this.answers.forEach((a: Answer) => {
      //   if (a.type == 16 || a.type == 30 || a.type == 31) a.valid = false;
      // })
      if (this.answers.length) {
        this.answers.every((a: Answer, index: number) => {
          // console.log(a);
          const page = this.surveyData().pages[this.surveyData().pages.findIndex((p: QuestionPage) => p.n_order == a.page)];
          let quest_f: Question = page.questions.find((q: Question) => q.id == a.question_id);
          if (quest_f != undefined) {
            // console.log("invalid: " + !a.valid, ", visibility: " + quest_f.visibility, ", visibility interpolation: " + quest_f.visibility_interpolation);
            if (quest_f.visibility && quest_f.visibility_interpolation) {
              if (!a.valid) {
                res(a.page);
                return false;
              }
            }
            if (index == this.answers.length - 1) {
              res(a.page)
              return false;
            }
          }
          return true;
        })
      } else res(1)

    })
    return n_page;
  }
  /**
   * return type question
   * @param id_type 
   * @returns 
   */
  getQuestionGroup(id_type: number) {
    return QuestionTypeGroups[id_type];
  }
  /**
   * check if survey has file type questions and has new data on it.
   * @returns 
   */
  async hasFileQuestions(answers: Array<Answer>) {
    return answers.some(a => [16, 30, 31].includes(a.type) && a.valid);
  }
  /**
   * upload files to AWS, update answers with the new file URL.
   */
  async processFileAnswers(answers: Array<Answer>) {
    return new Promise(async (resolve) => {
      let filter_answers = answers
        .filter((a: Answer) => a.type == 16 || a.type == 30 || a.type == 31) // file type
        .filter((a: Answer) => a.answer != null) // has answer
        .filter((a: Answer) => a.answer.file != null) // has answer 
        .filter((a: Answer) => a.answer["new_file"]) // check file

      if (filter_answers.length == 0) resolve(true)

      for (const [i, a] of filter_answers.entries()) {

        if (this.browserService.browser_name == 'safari') a.answer["file"] = this.questionFunctionalityService.dataURLtoFile(a.answer.base64, a.answer.file_name)

        const q = this.getQuestionById(a.question_id)
        if (q != undefined) this.loadingScreenService.change_message.next(this.transPipe.transform('SURVEY.UPLOADING_FILE').replace('${question}', q.title[this.trans.actualLang]))

        const base64String = (a.answer.file64 as string).split(",")[1]
        const data = { "blob64": base64String, "file_name": a.answer.file_name, "mime_type": a.answer.file.type }

        const r = await this.firebaseService.callFunction<{ Location?: string }>("uploadFileToAWS", data)
        if (r.data.Location != undefined) {
          a.answer["url"] = r.data.Location
          a.answer["new_file"] = false
        }
        this.saveTemporaryAnswerInIndexedDB()
        if (i == filter_answers.length - 1) resolve(true)
      }
    })
  }
  /**
   * @Author Daniel García
   * @Date 20-04-2022
   * check if exist registered answers in indexedbd for the current survey
   */
  checkPendingAnswers() {
    return new Promise((resolve, reject) => {

      this.dbService.getPendingAnswers(this.surveyData().id_survey).then((data) => {
        // console.log("getPendingAnswers", data);
        if (data.length > 0) {
          this.pending_answers = data;
          // this.executePendingAnswersProcess();
          resolve(true);
        } else resolve(false);

      });

    })

  }
  /**
   * @author Daniel García
   * @date 20-06-2023
   * Process pending answers
   * - Check if the responses have files or not, if you have upload them, then assign the file url to the answer
   * - Post answer to QServus
   */
  async processPendingAnswers() {
    this.processing_pending_answers = true
    this.processed_counter = 0
    // this.snack.openSnack(this.transPipe.transform('PROCESS_PENDING_ANSWERS.PLEASE_WAIT'), this.transPipe.transform('COMMON.OK'), 'right', 'top', ['snack-qservus-blue'], 3000);
      for (const [i, a] of this.pending_answers.entries()) {

        let pending_ans = a as PendingAnswers

        const durationSnapshot = this.getPendingDurationSnapshot(pending_ans);
        const validationResult = this.durationValidationService.evaluateDuration(
          this.surveyData()?.duration_validation,
          durationSnapshot
        );
        if (validationResult.durationSeconds !== null) durationSnapshot.duration_seconds = validationResult.durationSeconds;
        pending_ans.client_duration = durationSnapshot;
        pending_ans.duration_validation_reason = validationResult.reason ?? null;

        if (!validationResult.valid) {
          await this.durationValidationService.handleRejectedSubmission({
            survey: this.surveyData(),
            payload_snapshot: pending_ans,
            reason: validationResult.reason!,
            durationSeconds: validationResult.durationSeconds,
            timestamps: durationSnapshot,
            apiDomain: this.api_domain
          });
          if (a.id) await this.dbService.deletePendingAnswer(a.id);
          continue;
        }

        if (pending_ans.status == 0 || pending_ans.status == 2) { // do not consider successful submissions: 1
        // const has_files = await this.surveyService.hasFileQuestions(pending_ans.answers);
        if (this.answersHasFiles(pending_ans)) {

          this.processFileAnswers(pending_ans.answers).then((resp) => {
            // format file answers to only send URL
            pending_ans.answers.filter((a) => a.answer['file'] != undefined).forEach(a => a.answer = a.answer.url)

            this.qservusApiService.sendAnswer(pending_ans).then((post_answers_resp) => {
              this.handdleSuccess(post_answers_resp, a.id)
            }, error => this.handleError(i, error))

          })

        } else {

          this.qservusApiService.sendAnswer(pending_ans).then((post_answers_resp) => {
            this.handdleSuccess(post_answers_resp, a.id);
          }, error => this.handleError(a.id, error))

        }
      }
    }
  }
  /**
   * @author Daniel García
   * @date 10-05-2023
   * Add parameters in URL to extra_data property inside survey_data object
   */
  getExtraDataParametersInURL(survey_data: Survey) {
      
      if (!isArray(survey_data.extra_data)) survey_data['extra_data'] = []
      this.routerService.getParams().then((url_params) => {
        if (url_params != undefined) {
          Object.keys(url_params).forEach((param: string) => {

            if (survey_data.sensor_fields != undefined) {
              // check if sensor field with the same label exists
              const sensor_field = survey_data.sensor_fields.find((sf: SensorField) => (sf.label as string).toLowerCase() == param.toLowerCase());
              if (sensor_field != undefined) {
                // if exists, set value in sensor field
                sensor_field.answer = url_params[param];
              }
            }
            if (survey_data.extra_data.find((ed: ExtraData) => ed.property == param) == undefined)
              survey_data.extra_data.push({
                "property": param,
                "value": url_params[param]
              })
            else {
              // if exists, update value
              const extra_data = survey_data.extra_data.find((ed: ExtraData) => ed.property == param);
              if (extra_data != undefined) extra_data.value = url_params[param];
            }
          })
        }
      })

  }
  /**
   * @author Matías Villar
   * @date 29-11-2022
   * @description verify if route is in preview mode or not, and save in is_preview surveyService public variable
   */
  isPreview() {
    let route = this.router.url.split('/')[1].toLowerCase()
    this.is_preview = (route === "p")
    this.executeAutoPageAdvance = true
  }

  /**
   * @author Matías Villar
   * @date 08-02-2022
   * @description check query params and set answer in suervey...
   */
  async checkQueryParams() {
    if (this.qn) {
      //find question_id based on qn param (q_n)
      let flattened_questions = this.surveyData().pages.reduce((acc, val) => acc.concat(val.questions), [])
      let question_id = flattened_questions.find(q => q.q_n == this.qn).id

      this.partial_answers = {
        answers: [
          {
            question_id: question_id,
            answer: this.a
          }
        ],
        extra_data: null
      }
      await this.setAnswersInSurvey(this.partial_answers)
    }
  }

  /**
   * @author Matías Villar
   * @date 12-12-2022
   * @description Get Partial Answers from QServues api, save in storage and show in ui.
   */
  async getPartialAnswers(): Promise<boolean> {
    this.answers = []
    this.indexService.updateAnswerIndex([]);
    let partial_answers: Partial_answers

    partial_answers = await this.qservusApiService.getPartialAnswers(this.uuid_respondent, this.api_domain)
    //validate if partial_answers has errors (means that survey has not partial answers) and if user has answers by url params (this.partial_answers).
    if (partial_answers.hasOwnProperty('errors') && this.partial_answers) {
      await this.setAnswersInSurvey(this.partial_answers)
    } else {
      //compare this.partial_answers with partial_answers, if are different, replace this.partial_answers.answers with partial_answers.answers
      if (this.partial_answers?.answers.length > 0) {
        let exists_answer = false
        partial_answers.answers = partial_answers.answers.map(partial_answer => {
          let result = this.partial_answers.answers.find(answer => answer.question_id == partial_answer.question_id)
          if (result) {
            //save result as answer in partial_answers.answers
            exists_answer = true
            return result
          } else {
            return partial_answer
          }
        })
        if (!exists_answer) {
          //if answer not exists, add to array
          partial_answers.answers.push(this.partial_answers.answers[0])
        }
      }

      if (partial_answers.hasOwnProperty('answers')) {
        if (partial_answers.start_datetime && partial_answers.start_datetime != "") this.start_datetime = partial_answers.start_datetime
        else this.start_datetime = new Date().toISOString().slice(0, 19).replace('T', ' ');
        this.last_interaction_datetime = partial_answers.last_interaction_datetime ?? "";
        await this.setAnswersInSurvey(partial_answers)
        return true
      } else {
        return false
      }
    }
  }

  /**
   * @author Matías Villar
   * @date 09-02-2022
   * @description Set answers in survey, from partial_answers object.
   */
  async setAnswersInSurvey(partial_answers) {
    let prueba: Array<any> = []

    try {
      this.surveyData().pages.forEach(page => {
        page.questions.forEach(question => {
          let answer: Answer
          let formattedAnswerArray = []
          switch (question.type) {
            case 1://Texto
            case 2://Texto multilinea
            case 6:// fecha, hora
              //Encontrar la respuesta que proviene desde la API partial_answers.
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: answer.answer,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 4: //selección única
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                question.alternatives.forEach(alternative => {
                  if (this.findLabel(alternative.label, answer.answer)) {
                    alternative.hidden = false
                    alternative.visibility = true
                    prueba.push({
                      question: question,
                      type: question.type,
                      answer: alternative,
                      n_order: page.n_order,
                      matrix_type: question.matrix_type,
                      alternatives_id: alternative.id,
                      other_text: answer.answer_other ? answer.answer_other : undefined,
                      motive: answer.answer_motive ? answer.answer_motive : undefined
                    })
                  }
                })
              }
              break;
            case 7: //seleccion simple
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                question.alternatives.forEach(alternative => {
                  if (this.findLabel(alternative.label, answer.answer)) {
                    prueba.push({
                      question: question,
                      type: question.type,
                      answer: alternative,
                      n_order: page.n_order,
                      matrix_type: question.matrix_type,
                      alternatives_id: alternative.id,
                      other_text: answer.answer_other ? answer.answer_other : undefined,
                      motive: answer.answer_motive ? answer.answer_motive : undefined
                    })
                  }
                })
              }
              break;
            case 3: //seleccion multiple
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                formattedAnswerArray = []
                answer.answer.map(answer => {
                  //Recorremos las alternativas
                  question.alternatives.map(alternative => {
                    if (this.findLabel(alternative.label, answer)) {
                      alternative['checked'] = true
                      formattedAnswerArray.push(alternative)
                    }
                  })
                })
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: formattedAnswerArray,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 5: //Estrellas
            case 9: //Likert
            case 10: //nps
            case 12: //Caras
            case 14: //nps
            case 17:
            case 19: //SCALE_NATIONAL_STARS
            case 29: //linea con opciones.
            case 32: //NPS.
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                question.alternatives.forEach(alternative => {
                  if (alternative.value == answer.answer) {
                    prueba.push({
                      question: question,
                      type: question.type,
                      answer: answer.answer,
                      n_order: page.n_order,
                      matrix_type: question.matrix_type,
                      alternatives_id: alternative.id,
                      other_text: answer.answer_other ? answer.answer_other : undefined,
                      motive: answer.answer_motive ? answer.answer_motive : undefined
                    })
                  }
                })
              }
              break;
            case QuestionTypes.MAXDIFF:
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: answer.answer,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined,
                  block_assigned: answer.block_assigned ?? question.maxdiff_block
                })
              }
              break;
            case 18: //Ranking
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                formattedAnswerArray = []
                //ordenar respuestas por ranking
                let answerRanking = answer.answer;
                //recorrer el partial answers por el orden...
                answerRanking.map(answer => {
                  //encontramos si existe una alternativa que conincida con la respuesta
                  question.alternatives.map(alternative => {
                    this.findLabel(alternative.label, answer.alternative) ? formattedAnswerArray.push(alternative) : null
                  })
                })

                prueba.push({
                  question: question,
                  type: question.type,
                  answer: formattedAnswerArray,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 24: ///términos y condiciones
              //Encontrar la respuesta que proviene desde la API partial_answers.
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: answer.answer.toLowerCase() == 'true' ? true : false,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 27: //Si-No
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                question.alternatives.forEach(alternative => {
                  if (this.findLabel(alternative.label, answer.answer)) {
                    prueba.push({
                      question: question,
                      type: question.type,
                      answer: alternative.value,
                      n_order: page.n_order,
                      matrix_type: question.matrix_type,
                      alternatives_id: alternative.id,
                      other_text: answer.answer_other ? answer.answer_other : undefined,
                      motive: answer.answer_motive ? answer.answer_motive : undefined
                    })
                  }
                })
              }
              break;
            case 100: //matriz selección multiple
              let answer_formatted = []
              if (question.matrix_selection_type == 2) {
                //primero guardar en la respuesta la pregunta, con las alternativas como un aray vacío.
                question.matrix_questions.forEach(matrix_question => {
                  answer_formatted.push({
                    alternative: [],
                    question: matrix_question,
                    valid: true
                  })
                })

                //por cada answer formatted encontrar si tiene respuestas y agregarlas al arreglo
                answer_formatted.map(answer_formatted_elem => {
                  let answer_formated_multi = []
                  answer = partial_answers.answers.find(answer => answer.question_id == answer_formatted_elem.question.id)
                  if (answer) {
                    //recorro el answer_formatted para buscar pregunta por pregunta si EXISTE alguna respuesta (ANSWER) respondida por el usuario.
                    answer_formatted_elem.question.alternatives.forEach(alternative => {
                      //recorrer arreglo de respuesta.
                      answer.answer.forEach(user_answer => {
                        if (this.findLabel(alternative.label, user_answer)) {
                          answer_formated_multi.push(alternative)
                        } else if (user_answer == '-2') {
                          answer_formated_multi.push({
                            id: "-2",
                            label: question.no_apply_label,
                            value: "-2"
                          })
                        }
                      })
                    })
                    answer_formatted_elem.alternative = answer_formated_multi
                  }
                })

                //check if answer_formated has selected alternatives
                let count_answer_formated = 0
                answer_formatted.forEach(answer_formatted_elem => {
                  if (answer_formatted_elem.alternative.length > 0) {
                    count_answer_formated++
                  }
                })

                if (count_answer_formated > 0) {
                  prueba.push({
                    question: question,
                    type: question.type,
                    answer: answer_formatted,
                    n_order: page.n_order,
                    matrix_type: question.matrix_type,
                    alternatives_id: undefined,
                    other_text: undefined,
                    motive: undefined
                  })
                }
              } else { //matriz seleccion simple
                //recorrer las preguntas de la matriz,por cada pregunta evaluar si coincide el answer.question_id
                question.matrix_questions.forEach(matrix_question => {
                  answer = partial_answers.answers.find(answer => answer.question_id == matrix_question.id)
                  if (answer) {
                    matrix_question.alternatives.forEach(alternative => {
                      let has_alternative_selected = this.findLabel(alternative.label, answer.answer)
                      if (has_alternative_selected) {
                        answer_formatted.push({
                          alternative: alternative,
                          question: matrix_question
                        })
                      }
                    })
                  }
                })

                if (answer_formatted.length > 0) {
                  prueba.push({
                    question: question,
                    type: question.type,
                    answer: answer_formatted,
                    n_order: page.n_order,
                    matrix_type: question.matrix_type,
                    alternatives_id: undefined,
                    other_text: (answer && answer.answer_other) ? answer.answer_other : undefined,
                    motive: (answer && answer.answer_motive) ? answer.answer_motive : undefined
                  })
                }
              }
              break;
            case 30: //Archivo
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: answer.file,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 16: //foto
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              if (answer) {
                prueba.push({
                  question: question,
                  type: question.type,
                  answer: answer.image,
                  n_order: page.n_order,
                  matrix_type: question.matrix_type,
                  alternatives_id: undefined,
                  other_text: answer.answer_other ? answer.answer_other : undefined,
                  motive: answer.answer_motive ? answer.answer_motive : undefined
                })
              }
              break;
            case 35: //Conjoint
              answer = partial_answers.answers.find(answer => answer.question_id == question.id)
              
              if (answer && answer.answer) {
                try {
                  // Format from API: "pg1,op1;pg2,op2;pg3,op3"
                  const formattedAnswer = [];
                  const answerPairs = answer.answer.split(';');
                  
                  answerPairs.forEach(pair => {
                    const [pageStr, optionStr] = pair.split(',');
                    // Extract page and option numbers
                    const pageId = pageStr.replace('pg', '');
                    const optionId = optionStr.replace('op', '');
                    
                    const opt = question.conjoint_pages.find(x => x.id == pageId).options.find(x => x.option_id == optionId)
                    let opt_idx = question.conjoint_pages.find(x => x.id == pageId).options.findIndex(x => x.option_id == optionId)
                    if (opt.no_apply) opt_idx = -1
                    formattedAnswer.push({
                      page_id: parseInt(pageId),
                      option_id: optionId,
                      option: opt_idx
                    });
                  });
                  
                  if (formattedAnswer.length > 0) {
                    prueba.push({
                      question: question,
                      type: question.type,
                      answer: formattedAnswer,
                      n_order: page.n_order,
                      matrix_type: question.matrix_type || undefined,
                      alternatives_id: undefined,
                      other_text: answer.answer_other ? answer.answer_other : undefined,
                      motive: answer.answer_motive ? answer.answer_motive : undefined
                    });
                  }
                } catch (error) {
                  // console.log("Error parsing conjoint answer:", error);
                }
              }
              break;
            default:
              // console.log("Tipo de pregunta sin desarrollo:", question.type)
              break;
          }
        })
      })

      //only do if prueba.length > 0
      if (prueba.length > 0) {
        for (let i = 0; i < prueba.length; i++) {
          let response = await this.createResponseFromPartialEndpoint(
            prueba[i].question.id,
            prueba[i].answer,
            prueba[i].n_order,
            prueba[i].type,
            prueba[i].matrix_type,
            prueba[i].other_text,
            prueba[i].motive,
            prueba[i].alternatives_id,
            prueba[i].block_assigned
          )
          if (response.answer) {
            await this.answers.push(response)
          }
        }
        // console.log('partial_answers.answers',partial_answers.answers)
        // console.log('this.survey_data',this.surveyData().pages[0].questions)

        await this.saveTemporaryAnswerInIndexedDB()
        this.applyStoredMaxdiffDistributions();
        this.indexService.updateAnswerIndex(this.answers);
      }

    } catch (error) {
      // console.log('catch', error)
      partial_answers = null
    }
  }

  /**
   * @author Matías Villar
   * @date 12-12-2022
   * @description Create response from partial answer
   * @param 
   */
  async createResponseFromPartialEndpoint(question_id: number, answer: Answer, n_order: number, type: number, matrix_type: number, other_text: string, motive: string, alternative_id?: number, block_assigned?: number): Promise<Answer> {
    let question_answer: any

    if (type == 16) {
      question_answer = await this.getImageFromUrl(answer)
    } else if (type == 30) {
      question_answer = await this.getFileFromUrl(answer)
    } else {
      question_answer = answer
    }

    let response: Answer = {
      page: n_order,
      question_id: question_id,
      type: type,
      answer: question_answer,
      valid: true
    }

    alternative_id ? response['alternative_id'] = alternative_id : null
    matrix_type ? response['matrix_type'] = matrix_type : null
    answer.motive ? response['motive'] = answer.motive : null
    other_text ? response['other_text'] = other_text : null
    motive ? response['motive'] = motive : ""
    if (block_assigned != undefined && block_assigned != null) {
      response['block_assigned'] = block_assigned;
    }
    return response
  }

  private shouldSkipPendingModalOnce(): boolean {
    const shouldSkip = this.localStorageService.get(this.skipPendingModalKey) === '1';
    if (shouldSkip) this.localStorageService.remove(this.skipPendingModalKey);
    return shouldSkip;
  }

  /**
   * @author Matías Villar
   * @date 14-12-2022
   * @description show Modal requesting to continue with the survey or start a new one
   */
  showModalPendingAnswer() {
    const diag = this.dialog.open(ModalConfirmComponent, {
      data: {
        title: '',
        subtitle: '',
        message: this.transPipe.transform('MODAL_LOAD_RESP.MESSAGE'),
        button_no: this.transPipe.transform('MODAL_LOAD_RESP.NO'),
        button_yes: this.transPipe.transform('MODAL_LOAD_RESP.YES')
      },
      disableClose: true,
      panelClass: "modal-confirm"
    })
    diag.afterClosed().subscribe(async (resp) => {
      if (!resp) {
        this.resetTemporarySurveyData();
        if (this.surveyData().allow_partial_answers) this.savePartialAnswersInFirebase();
        //check if there are query params after closing modal
        await this.checkQueryParams()
        this.checkPageLogicToShow(false);
        this.checkDerivationLogics();
        if (this.surveyData().logic_operator) this.checkSensorLogicToShow()
        this.answers_changed.next(true);
        this.call_check_progress.next(true);
        this.executeAutoPageAdvance = true
      }
    })
  }

  /**
   * @author Matías Villar
   * @date 15-12-2022
   * @description transform url image to base64
   * @param imgUrl url of image
   * @returns { base64 : base 46 image type,
   * file : file type 'blob',
   * size : size of image,
   * type : type of image}
   */
  async getImageFromUrl(imgUrl) {
    let res: any = await this.networkService.getImgBlob(imgUrl)
    let blob = new Blob([res], { type: res.type });
    let extension = res.type.split('/')[1]
    const file = new File([blob], `image.${extension}`, { type: res.type })

    if (this.partialAnswersfileValidator(extension)) {
      return {
        base64: await this.blobToBase64(blob),
        file: file,
        size: res.size,
        type: res.type,
        file_name: `image.${extension}`,
        file_prev_ext: "",
        file_ext: extension,
        file_size: res.size
      }
    } else {
      return null
    }

  }

  /**
   * @author Matías Villar
   * @date 02-02-2022
   * @description transform url file to blob
   * @param fileUrl url of image
   * @returns { blob : base 46 image type,
  * file : file type 'blob',
  * size : size of image,
  * type : type of image}
  */
  async getFileFromUrl(fileUrl) {
    let res: any = await this.networkService.getImgBlob(fileUrl)
    let type = `application/${fileUrl.split('.').pop()}`
    let extension = fileUrl.split('.').pop()
    let blob = new Blob([res], { type: type });
    const file = new File([blob], `file.${extension}`, { type: type })

    if (this.partialAnswersfileValidator(extension)) {
      return {
        base64: await this.blobToBase64(blob),
        file: file,
        file_ext: extension,
        file_name: `file.${extension}`,
        file_size: res.size,
        file_type: type,
        file_prev_ext: ""

      }
    } else {
      return null
    }

  }

  /**
   * @author Daniel García
   * @date 20-06-2023
   * check if answer list include file type questions
   * @param pending_ans 
   * @returns 
   */
  answersHasFiles(pending_ans: PendingAnswers): boolean {
    const finded_with_file = pending_ans.answers.find((a: Answer) => a.answer['file']);
    return finded_with_file != undefined;
  }

  /**
   * @author Daniel García
   * @date 20-06-2023
    * handle success response on request
    * @param resp 
    * @param id pending answers id 
    */
  handdleSuccess(resp: any, id: number) {
    this.processed_counter++;
    if (resp.uuid != undefined) { // update counters
      // this.surveyService.pending_answers[index].status = 1;
      // this.dbService.updatePendingAnswers(this.surveyService.pending_answers[index].id, this.surveyService.pending_answers[index])
      this.dbService.deletePendingAnswer(id);
      const idx = this.pending_answers.indexOf((pa: PendingAnswers) => pa.id == id);
      this.pending_answers.splice(idx, 1);
    }
    if (this.pending_answers.length == 0) {
      const text = this.processed_counter > 1 ? this.transPipe.transform('PROCESS_PENDING_ANSWERS.END_WITH_P').replace('${count}', this.processed_counter) : this.transPipe.transform('PROCESS_PENDING_ANSWERS.END_WITH')
      this.snack.openSnack(text, this.transPipe.transform('COMMON.CLOSE'), 'right', 'top', ['snack-qservus-blue'], 3000);
      this.processing_pending_answers = false
    }
  }

  /**
   * @author Daniel García
   * @date 20-06-2023
   * handle error on request
   * @param id pending answers id 
   * @param error 
   */
  handleError(id: number, error: any) {
    this.processed_counter++;
    // this.surveyService.pending_answers[index].status = 2;
    // this.dbService.updatePendingAnswers(this.surveyService.pending_answers[index].id, this.surveyService.pending_answers[index])
    this.dbService.deletePendingAnswer(id);
    const idx = this.pending_answers.indexOf((pa: PendingAnswers) => pa.id == id);
    this.pending_answers.splice(idx, 1);

    if (this.pending_answers.length == 0) { // check if it's the last one
      const text = this.processed_counter > 1 ? this.transPipe.transform('PROCESS_PENDING_ANSWERS.END_WITH_P').replace('${count}', this.processed_counter) : this.transPipe.transform('PROCESS_PENDING_ANSWERS.END_WITH')
      this.snack.openSnack(text, this.transPipe.transform('COMMON.CLOSE'), 'right', 'top', ['snack-qservus-blue'], 3000);
      this.processing_pending_answers = false
    }
  }
  /**
   * @author Matías Villar
   * @date 15-12-2022
   * @description Transform Blob file to base64.
   * @param file blob type file
   * @returns base64 file.
   */
  async blobToBase64(file) {
    let result_base64 = await new Promise((resolve) => {
      let fileReader = new FileReader();
      fileReader.onload = (e) => resolve(fileReader.result);
      fileReader.readAsDataURL(file);
    });
    return result_base64;
  }

  /**
   * @author Matías Villar
   * @date 19-12-2022
   * @description Determine if partial_answer exists in option or answer label.
   * @param label Translate option or answer from question.
   * @param search partial_answer.
   * @returns Boolean null if not exists, true if exists.
   */
  findLabel(label: Translate, search: string): Boolean {
    let result = null
    Object.keys(label).forEach(key => {
      if (label[key] == search) result = true
    })
    return result
  }

  partialAnswersfileValidator(extension: string): Boolean {
    let fileExtension = extension.replace('.', '')
    let types: Array<String> = ["doc", "xls", "xlsx", "ppt", "txt", "pdf", "csv", "jpe", "jpg", "jpeg",
      "gif", "png", "bmp", "icon", "svg", "svgz", "tif", "tiff", "ai", "drw", "pct", "xcf", "psd", "raw", "webp"]
    return (types.find(extension => extension == fileExtension)) ? true : false
  }

  /**
     * get page to jump if alternative has jump_to property
     * @returns number of page to jump
     * @author Matías Villar
     * @date 2023-10-06
     */
	alternativeJumpTo() : number {
		let numberReturn = -1
		//get all questions of current page
		let questions = this.current_page.questions

		//order questions by order property
		questions.sort( (a: Question, b: Question) => a.order - b.order )
		
		//iterate over questions
		questions.map( (question: Question) => {
			//compare if some answers equals to question
			let answers = this.answers.find( (answer: Answer) => answer.question_id == question.id)
			if((	
					question.type == 5 ||
					question.type == 7 ||
					question.type == 9 ||
					question.type == 10 ||
					question.type == 12 ||
					question.type == 14 ||
					question.type == 17 ||
					question.type == 19 ||
					question.type == 27 ||
					question.type == 29 ||
					question.type == 32
				) && (answers)) {
				let alternative_id = answers.alternative_id
        if (alternative_id == undefined) alternative_id = answers.answer.id;
				//compare if answer is equal to alternative has jump_to
				let alternative = question.alternatives.find( (alternative: Alternative) => alternative.id == alternative_id)
				if( alternative?.jump_to != null){
					numberReturn = alternative.jump_to - 1
				}else{
					numberReturn = -1
				}
			}else if(question.type == 4 && answers) {
				//select single question
				let alternative_id = answers.answer.id
        if (alternative_id != '-2') {
				let alternative = question.alternatives.find( (alternative: Alternative) => alternative.id == alternative_id)
				
				if (alternative?.jump_to != null) {
					numberReturn = alternative.jump_to - 1
				} else {
					numberReturn = -1
				}
      }
			} else if(question.type == 3 && answers) {
				//iterate over answer array of selection multiple question
				//only if answer.answer is not empty array
				if (answers.answer.length > 0) {
					// get derivation_type of question
					let derivation_type = question.derivation_type
          
					if (derivation_type == 1) {
						//proroty jump to
						let jump: { jump_to: number, priority: number } = { jump_to: -1, priority: -1 }
						//order answer by id
						answers.answer.sort( (a: any, b: any) => a.id - b.id )

						answers.answer.map( (answer: any) => {					
							//get id of answer
							let alternative_id = answer.id
              if (alternative_id != '-2') {
                let alternative = question.alternatives.find( (alternative: Alternative) => alternative.id == alternative_id)

                if (alternative?.jump_to != null) {
                  if (jump.jump_to == -1) {
                    jump.jump_to = alternative.jump_to
                    jump.priority = alternative.priority
                  } else if (jump.priority >= alternative.priority) {
                    jump.jump_to = alternative.jump_to
                    jump.priority = alternative.priority
                  }
                }
              }
						})
						numberReturn = jump.jump_to - 1
					} else if (derivation_type == 2) {
						//random jump to
						let jump_to : Array<number> = []
						answers.answer.map( (answer: any) => {					
							//get id of answer
							let alternative_id = answer.id
              if (alternative_id != '-2') {
                let alternative = question.alternatives.find( (alternative: Alternative) => alternative.id == alternative_id)
							if (alternative?.jump_to != null) {
								jump_to.push(alternative.jump_to)
							}
            }
						})
						//validate that jump_to is not empty
						if (jump_to.length > 0) {
							//select randomly one element of jump_to array and console.log the element of jump_to array
							let random = Math.floor(Math.random() * jump_to.length)
							//set numberReturn with random element of jump_to array
							numberReturn = jump_to[random] - 1
						}
					}
				}				
			}
		})
		return numberReturn
	}

	/**
     * get page to jump if page has jump_to property
     * @returns number of page to jump
     * @author Matías Villar
     * @date 2023-10-17
     */
	pageJumpTo() {
		let numberReturn = -1
		//get property jump_to of current page (if exists)
		let jump_to = this.current_page.jump_to
		//compare if jump_to is not null
		if( jump_to != null)
			numberReturn = jump_to - 1
		
		return numberReturn
	}

  /**
     * change visibility of skipped pages
     * @author Matías Villar
     * @date 2023-10-18
     */
	private changeVisibilityOfSkippedPagesFrom(sourcePageIndex: number, jump_to: number): boolean {
		let visibilityChanged = false;
		this.surveyData().pages.forEach((page: QuestionPage, page_index) => {
      // page.questions.forEach((question: Question) => question.visibility = question.initial_visibility !== undefined ? question.initial_visibility : true);
      //if page is between current page and jump_to page and is not current page.
      if ( page_index > sourcePageIndex && page_index < jump_to  && page_index !== sourcePageIndex ) {
			// if (page_index > current_page_index && page_index < (jump_to + 1) && page_index !== current_page_index) {
				page.questions.forEach((question: Question) => {
					//console.log('change visibility n_order =', page.n_order)
					if (question.initial_visibility == undefined ) {
						//console.log('initial visivility undefined')
						question.initial_visibility = question.visibility
					}
          if (question.visibility !== false) {
            visibilityChanged = true;
          }
					question.visibility = false
				})
				page.jump_to_visibility = false
			}
		})
    return visibilityChanged;
	}

   /**
     * reset visibility of skipped pages
     * @author Matías Villar
     * @date 2023-10-18
     */
	private resetVisibilityOfSkippedPagesAll(): boolean {
    let visibilityChanged = false;
		this.surveyData().pages
    .forEach((page: QuestionPage) => {
			if( page.jump_to_visibility == false ){
				page.questions.forEach((question: Question) => {
          if (question.visibility !== question.initial_visibility) {
            visibilityChanged = true;
          }
					question.visibility = question.initial_visibility
          if (question.visibility_interpolation !== question.initial_visibility_interpolation) {
            visibilityChanged = true;
          }
					question.visibility_interpolation = question.initial_visibility_interpolation
				})
			}
			page.jump_to_visibility = true
		})
    return visibilityChanged;
	}

  private applyJumpVisibilityFromMap(): boolean {
    let visibilityChanged = false;
    this.activeJumpTargets.forEach((jumpTo, sourceIndex) => {
      visibilityChanged = this.changeVisibilityOfSkippedPagesFrom(sourceIndex, jumpTo) || visibilityChanged;
    });
    return visibilityChanged;
  }

	private cleanupAnswersForSkippedPages(targetPageIndex: number): boolean {
		if (!this.answers?.length || !this.surveyData?.() || !Array.isArray(this.surveyData().pages)) {
			return false;
		}

		const pages = this.surveyData().pages;
		const shouldRemoveQuestionIds = new Set<number>();

		pages.forEach((page: QuestionPage, index: number) => {
			if (
				index > this.current_page_index &&
				(targetPageIndex >= pages.length || index < targetPageIndex)
			) {
				page.questions?.forEach((question: Question) => {
					if (question && typeof question.id === 'number') {
						shouldRemoveQuestionIds.add(question.id);
					}
				});
			}
		});

		if (!shouldRemoveQuestionIds.size) {
			return false;
		}

		const initialLength = this.answers.length;
		this.answers = this.answers.filter((answer: Answer) => {
			if (!answer?.question_id) {
				return true;
			}

			return !shouldRemoveQuestionIds.has(answer.question_id);
		});

		const answersChanged = this.answers.length !== initialLength;
		if (answersChanged) {
			this.indexService.updateAnswerIndex(this.answers);
		}

		return answersChanged;
	}

	private setBranchingLoadingState(active: boolean): void {
		if (active) {
			this.branchingLoadingDepth++;
			if (this.branchingLoadingDepth > 1) {
				return;
			}

			const nextLoading = [...this.loadingPage(), { key: this.branchingLoadingKey, value: true }];
			this.loadingPage.set(nextLoading);
			return;
		}

		if (this.branchingLoadingDepth === 0) {
			return;
		}

		this.branchingLoadingDepth = Math.max(0, this.branchingLoadingDepth - 1);
		if (this.branchingLoadingDepth > 0) {
			return;
		}

		Promise.resolve().then(() => {
			const currentLoading = [...this.loadingPage()];
			const existingIndex = currentLoading.findIndex(entry => entry.key === this.branchingLoadingKey);
			if (existingIndex === -1) {
				return;
			}

			currentLoading.splice(existingIndex, 1);
			this.loadingPage.set(currentLoading);
		});
	}

	private persistCleanedAnswersSnapshot(): void {
		if (this.answers.length) {
			this.saveTemporaryAnswerInIndexedDB().catch((error) => {
				console.warn('Failed to persist cleaned temporary answers in IndexedDB:', error);
			});
		} else {
			this.dbService.deleteTemporaryAnswersBySurveyUUID()
				.catch((error) => console.warn('Failed to clear temporary answers after cleanup:', error));
		}

		if (this.surveyData()?.allow_partial_answers) {
			this.savePartialAnswersInFirebase().catch((error) => {
				console.warn('Failed to persist cleaned partial answers in Firebase:', error);
			});
		}
	}

  /**
   * check derivation logic
   * @author Matías Villar
   * @date 2024-01-19
   */
	checkDerivationLogics() {
		this.setBranchingLoadingState(true);
		let answersRemoved = false;

		try {
			const alternativeTarget = this.alternativeJumpTo();
			const pageTarget = this.pageJumpTo();
			const nextJumpTarget = alternativeTarget != -1 ? alternativeTarget : pageTarget;

      if (nextJumpTarget != -1) {
        this.activeJumpTargets.set(this.current_page_index, nextJumpTarget);
      } else {
        this.activeJumpTargets.delete(this.current_page_index);
      }

			let visibilityChanged = this.resetVisibilityOfSkippedPagesAll();
			visibilityChanged = this.applyJumpVisibilityFromMap() || visibilityChanged;

			if (nextJumpTarget != -1) {
				answersRemoved = this.cleanupAnswersForSkippedPages(nextJumpTarget) || answersRemoved;
			}

			this.alternative_jump_to = alternativeTarget;
			this.page_jump_to = pageTarget;

      this.storageManagerService.storeSurveyData(this.uuid_survey, this.surveyData()).catch(error => {
        console.error('Failed to store survey construct:', error);
      });

			if (visibilityChanged) {
				this.progressVisibilityEpoch++;
				this.call_check_progress.next(true);
			}

			if (answersRemoved) {
				this.touchLastInteractionTimestamp();
				this.persistCleanedAnswersSnapshot();
				this.call_check_progress.next(true);
			}
		} finally {
			this.setBranchingLoadingState(false);
		}
	}

  /**
   * Updates the extra data of a survey question.
   * 
   * @param question - The question object to update the extra data for.
   * @param response - The response value to set for the question.
   */
  updateSurveyExtraData(question: Question, response: any) {
    let extra_data = this.surveyData().extra_data != undefined ? this.surveyData().extra_data : []

    if (response.answer == undefined) {

      let extra_data_index = extra_data.findIndex((ed: ExtraData) => ed.property == question.title[this.trans.actualLang].toLowerCase())
      if (extra_data_index != -1)  extra_data.splice(extra_data_index, 1)

    } else {

      let extra_data_index = extra_data.findIndex((ed: ExtraData) => ed.property == question.title[this.trans.actualLang].toLowerCase())
      if (extra_data_index != -1) {
        extra_data[extra_data_index].value = response.answer.value
      } else {
        extra_data.push({ property: question.title[this.trans.actualLang].toLowerCase(), value: response.answer.value })
      }

    }
    this.surveyData()['extra_data'] = extra_data
    this.storageManagerService.storeSurveyData(this.uuid_survey, this.surveyData()).catch(error => {
      console.error('Failed to store survey construct with extra data:', error);
    }); 
  } 

  /**
   * Apply survey theme colors
   */
  applySurveyThemeColors() {
    if (this.surveyData()) {
      const primary = this.surveyData().primary_color || '#f9a100';
      const secondary = this.surveyData().secondary_color || '#5cb85c';
      document.documentElement.style.setProperty('--survey-primary-color', primary);
      document.documentElement.style.setProperty('--survey-secondary-color', secondary);
    }
  }

  /**
   * Rebuild survey indexes for fast lookups
   * Called after survey data or answers change
   * P0-03 optimization
   */
  rebuildSurveyIndexes(): void {
    if (this.surveyData()) {
      this.indexService.rebuildIndexes(this.surveyData(), this.answers);
    }
  }
}



